import type { Lang } from "@/content/tracks";

export interface Bootcamp {
  id: string;
  slug: string;
  track: string;
  level: "beginner" | "intermediate" | "advanced";
  duration_weeks: number;
  price_eur: number;
  price_xof: number;
  is_new: boolean;

  /** Phase 6: cohort lifecycle. When "upcoming", detail page hides price/installments and shows waitlist only. */
  status?: "upcoming" | "open" | "sold_out" | "archived";
  /** ISO date of the next cohort start, used when status === "upcoming". */
  next_cohort_at?: string;

  title: { en: string; fr: string; es: string | null; pt: string | null };
  card_tagline: { en: string; fr: string; es: string | null; pt: string | null };
  subtitle: { en: string; fr: string; es: string | null; pt: string | null };

  framework: string;
  framework_expansion: { en: string; fr: string; es: string | null; pt: string | null };
  designed_for: { en: string[]; fr: string[]; es: string[] | null; pt: string[] | null };
  modules: { en: string[]; fr: string[]; es: string[] | null; pt: string[] | null };
  ebook_title: { en: string | null; fr: string | null; es: string | null; pt: string | null };
  /** Optional intro video (e.g. HeyGen-generated presenter). Renders as overlay on the cover. */
  intro_video_url?: string;
  /** Flagship marker — pinned to top of catalog/track listings and rendered with a "Flagship" badge. */
  is_flagship?: boolean;
}

// ============================================================================
// TRACK 1: STARTUP LAUNCH (7 bootcamps — incl. flagship Diaspora Venture Bootcamp)
// ============================================================================

export const bootcamps: Bootcamp[] = [
  {
    id: "bc-46",
    slug: "diaspora-venture-bootcamp",
    track: "startup-launch",
    level: "intermediate",
    duration_weeks: 16,
    price_eur: 899,
    price_xof: 590000,
    is_new: true,
    is_flagship: true,
    status: "upcoming",
    next_cohort_at: "2026-08-03",
    intro_video_url: "/__l5e/assets-v1/c85ce3fe-7fa1-4e6a-96ee-40350521ca2b/873cbacf0b5b497fbe65598f8fb25aa7.mp4",
    title: {
      en: "Diaspora Venture Bootcamp",
      fr: "Bootcamp Diaspora Venture",
      es: null, pt: null
    },
    card_tagline: {
      en: "16-week venture studio for the African diaspora",
      fr: "Studio de venture de 16 semaines pour la diaspora africaine",
      es: null, pt: null
    },
    subtitle: {
      en: "An asynchronous venture-building bootcamp for francophone African diaspora professionals. 16 weeks to validate, structure, launch, finance, and govern an Africa-connected venture with evidence — capstone dossier, pitch deck, recorded pitch, and 90-day execution roadmap.",
      fr: "Un bootcamp asynchrone pour les professionnels francophones de la diaspora africaine. 16 semaines pour valider, structurer, lancer, financer et gouverner une venture connectée à l'Afrique avec des preuves — dossier capstone, pitch deck, pitch enregistré et feuille de route d'exécution sur 90 jours.",
      es: null, pt: null
    },
    framework: "D.I.A.S.P.O.R.A.",
    framework_expansion: {
      en: "Discipline, Intention, Architecture, Sectors, Pilot, Operations, Risk, Action",
      fr: "Discipline, Intention, Architecture, Secteurs, Pilote, Opérations, Risque, Action",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Francophone African diaspora professionals building Africa-connected ventures",
        "First-time founders who need structured validation and venture architecture",
        "Diaspora entrepreneurs with informal activity needing governance, pricing, and operations discipline",
        "Executives and managers pivoting to entrepreneurship, advisory, or market-entry projects",
        "SME owners expanding into an African market or working with local partners",
        "Diaspora investors and project sponsors evaluating opportunities more rigorously"
      ],
      fr: [
        "Professionnels francophones de la diaspora africaine qui construisent des ventures connectées à l'Afrique",
        "Fondateurs débutants ayant besoin d'une validation structurée et d'une architecture de venture",
        "Entrepreneurs de la diaspora avec une activité informelle qui ont besoin de gouvernance, pricing et discipline opérationnelle",
        "Cadres et managers en pivot vers l'entrepreneuriat, le conseil ou des projets d'entrée de marché",
        "Dirigeants de PME en expansion sur un marché africain ou avec des partenaires locaux",
        "Investisseurs et sponsors de la diaspora évaluant des opportunités avec rigueur"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "Orientation — From Diaspora Intention to Venture Discipline",
        "Diaspora Advantage, Market Reality & Opportunity Selection",
        "Market Selection & Problem Discovery Across Borders",
        "Customer Discovery, Validation & Evidence Standards",
        "Business Model Design — From Problem to Offer",
        "Start Your Own Business I — Founder Decision, Legal Form & Venture Architecture",
        "Start Your Own Business II — Revenue, Pricing & Unit Economics",
        "Start Your Own Business III — MVP, Pilot & Proof of Demand",
        "Cross-Border Legal, Tax, Compliance & Governance Risk",
        "Digital Operating System, AI Productivity & Remote Execution",
        "Go-to-Market, Sales & Trust-Building Across Communities",
        "Partnerships, Local Operators & Supplier Due Diligence",
        "Funding Strategy, Diaspora Capital, Grants, Debt & Investor Readiness",
        "Leadership, Team, Ethics, Impact & Local Value Creation",
        "Pitch, Capstone Dossier & Demo-Day Simulation",
        "Final Online Demo Day, 90-Day Launch Roadmap & Alumni Network"
      ],
      fr: [
        "Orientation — De l'intention diaspora à la discipline venture",
        "Avantage diaspora, réalité du marché et sélection d'opportunités",
        "Sélection de marché et découverte du problème à distance",
        "Découverte client, validation et standards de preuve",
        "Conception du modèle d'affaires — du problème à l'offre",
        "Créer son entreprise I — Décision fondateur, forme juridique et architecture de venture",
        "Créer son entreprise II — Revenus, pricing et économie unitaire",
        "Créer son entreprise III — MVP, pilote et preuve de demande",
        "Risque juridique, fiscal, conformité et gouvernance transfrontaliers",
        "Système d'exploitation digital, productivité IA et exécution à distance",
        "Go-to-market, ventes et confiance au sein des communautés",
        "Partenariats, opérateurs locaux et due diligence fournisseurs",
        "Stratégie de financement, capital diaspora, subventions, dette et investor-readiness",
        "Leadership, équipe, éthique, impact et création de valeur locale",
        "Pitch, dossier capstone et simulation de demo-day",
        "Demo Day final en ligne, feuille de route 90 jours et réseau alumni"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "The Diaspora Founder's Playbook: Building Africa-Connected Ventures with Evidence",
      fr: "Le manuel du fondateur diaspora : bâtir des ventures connectées à l'Afrique avec des preuves",
      es: null, pt: null
    }
  },
  {
    id: "bc-17",
    slug: "starting-business-zero-investment",
    track: "startup-launch",
    level: "beginner",
    duration_weeks: 5,
    price_eur: 149,
    price_xof: 97800,
    is_new: false,
    intro_video_url: "/videos/intro-zero-investment.mp4",
    title: {
      en: "Starting a Business with Zero Investment",
      fr: "Créer une entreprise sans capital de départ",
      es: null, pt: null
    },
    card_tagline: {
      en: "Lean bootstrapping & AI validation",
      fr: "Bootstrap lean et validation IA",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered lean startup strategies, neuroscience-driven bootstrapping, and high-impact growth techniques",
      fr: "Maîtriser les stratégies lean startup augmentées par l'IA, le bootstrap appuyé par les neurosciences, et les techniques de croissance à fort impact",
      es: null, pt: null
    },
    framework: "L.E.A.N.",
    framework_expansion: {
      en: "Launch, Earn, Automate, Network",
      fr: "Lancer, Gagner, Automatiser, Réseauter",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Aspiring entrepreneurs and business owners with limited capital",
        "Startup founders looking to launch lean and scalable ventures",
        "Freelancers, creators, and side hustlers monetizing their skills",
        "Professionals transitioning from employment to entrepreneurship"
      ],
      fr: [
        "Porteurs de projet et dirigeants avec un capital limité",
        "Fondateurs de startups cherchant à lancer des aventures lean et scalables",
        "Freelances, créateurs et side-hustlers monétisant leurs compétences",
        "Professionnels en transition du salariat vers l'entrepreneuriat"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Entrepreneurial Thinking & Problem Solving",
        "Mastering the L.E.A.N. Framework for Bootstrapping Success",
        "Validating a Business Idea Without Spending Money",
        "Monetizing Your Skills & Digital Assets",
        "Low-Cost & Free Marketing Strategies for Growth",
        "Automation & AI-Powered Scaling Strategies",
        "Business Networking & Finding Investors Without Investment",
        "Final Startup Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la pensée entrepreneuriale et de la résolution de problèmes",
        "Maîtriser le cadre L.E.A.N. pour réussir le bootstrap",
        "Valider une idée d'affaires sans dépenser d'argent",
        "Monétiser ses compétences et actifs numériques",
        "Stratégies marketing gratuites et low-cost pour la croissance",
        "Automatisation et stratégies de scale augmentées par l'IA",
        "Networking business et trouver des investisseurs sans investir",
        "Stratégie finale de lancement et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Bootstrapping: How to Launch a Business with Zero Investment",
      fr: "Bootstrap augmenté par l'IA : lancer une entreprise sans capital de départ",
      es: null, pt: null
    }
  },
  {
    id: "bc-21",
    slug: "startup-confidence-launch-assurance",
    intro_video_url: "/__l5e/assets-v1/786555f4-36ba-4a0b-a78d-762a3b98cc5f/4f842f51767d4d70b1fba458d701688c.mp4",
    track: "startup-launch",
    level: "beginner",
    duration_weeks: 4,
    price_eur: 99,
    price_xof: 65000,
    is_new: false,
    title: {
      en: "Startup Confidence",
      fr: "Confiance startup",
      es: null, pt: null
    },
    card_tagline: {
      en: "Pitch with authority",
      fr: "Pitcher avec autorité",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered confidence strategies, neuroscience-driven mindset shifts, and high-impact business assurance techniques",
      fr: "Maîtriser les stratégies de confiance augmentées par l'IA, les bascules d'état d'esprit appuyées par les neurosciences, et les techniques d'assurance à fort impact",
      es: null, pt: null
    },
    framework: "C.O.N.F.I.D.E.N.C.E.",
    framework_expansion: {
      en: "Clarity, Ownership, Networking, Focus, Insight, Discipline, Execution, Negotiation, Communication, Endurance",
      fr: "Clarté, Appropriation, Networking, Focus, Insight, Discipline, Exécution, Négociation, Communication, Endurance",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Aspiring entrepreneurs and startup founders building self-belief",
        "Business owners overcoming fear, doubt, and impostor syndrome",
        "Professionals transitioning to entrepreneurship",
        "Anyone seeking confidence in pitching, selling, and leading"
      ],
      fr: [
        "Porteurs de projet et fondateurs construisant leur confiance en soi",
        "Dirigeants dépassant peur, doute et syndrome de l'imposteur",
        "Professionnels en transition vers l'entrepreneuriat",
        "Toute personne voulant gagner en confiance pour pitcher, vendre et diriger"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Confidence & Entrepreneurial Mindset",
        "Mastering the C.O.N.F.I.D.E.N.C.E. Framework for Startup Success",
        "Overcoming Fear & Risk in Business Launch",
        "Building a Business Model That Works",
        "Confidence in Sales, Pitching & Public Speaking",
        "Resilience & Mental Strength for Startup Success",
        "Strategic Networking & Business Relationship Building",
        "Final Confidence Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la confiance et de l'état d'esprit entrepreneurial",
        "Maîtriser le cadre C.O.N.F.I.D.E.N.C.E. pour réussir son lancement",
        "Dépasser la peur et le risque au moment du lancement",
        "Construire un business model qui fonctionne",
        "Confiance dans la vente, le pitch et la prise de parole",
        "Résilience et force mentale pour la réussite startup",
        "Networking stratégique et construction de relations business",
        "Stratégie finale de confiance et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Confidence: How to Launch Your Startup with Assurance",
      fr: "Confiance augmentée par l'IA : lancer sa startup avec assurance",
      es: null, pt: null
    }
  },
  {
    id: "bc-45",
    slug: "failure-not-option-successful-day-one",
    intro_video_url: "/__l5e/assets-v1/24f5bb60-3023-4469-9a18-38b876193cbb/fd6b2f7b1089402da55596aebda67957.mp4",
    track: "startup-launch",
    level: "beginner",
    duration_weeks: 5,
    price_eur: 149,
    price_xof: 97800,
    is_new: true,
    title: {
      en: "Failure is Not an Option",
      fr: "L'échec n'est pas une option",
      es: null, pt: null
    },
    card_tagline: {
      en: "Launch strong from day 1",
      fr: "Démarrer fort dès le premier jour",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered business launch strategies, neuroscience-driven resilience, and high-impact startup execution techniques",
      fr: "Maîtriser les stratégies de lancement augmentées par l'IA, la résilience appuyée par les neurosciences, et les techniques d'exécution startup à fort impact",
      es: null, pt: null
    },
    framework: "S.T.A.R.T.",
    framework_expansion: {
      en: "Strategy, Testing, Adaptability, Resilience, Traction",
      fr: "Stratégie, Tests, Adaptabilité, Résilience, Traction",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "First-time entrepreneurs and business owners seeking to start strong",
        "Aspiring founders avoiding common startup pitfalls and costly mistakes",
        "Startup teams and side hustlers preparing for business growth",
        "Business professionals transitioning into entrepreneurship"
      ],
      fr: [
        "Entrepreneurs débutants et dirigeants voulant démarrer fort",
        "Porteurs de projet évitant les pièges classiques et les erreurs coûteuses",
        "Équipes startup et side-hustlers préparant leur croissance",
        "Professionnels en transition vers l'entrepreneuriat"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Resilient Entrepreneurs & Startup Success",
        "Mastering the S.T.A.R.T. Framework for Business Success",
        "AI-Powered Business Planning & Lean Startup Execution",
        "Market Validation & Customer Testing Before Launch",
        "Funding & Bootstrapping Strategies for Early-Stage Startups",
        "High-Impact Sales & Marketing Strategies from Day 1",
        "Building a Resilient Company Culture & Scalable Team",
        "Final Business Launch Strategy & AI Startup Validator"
      ],
      fr: [
        "Les neurosciences des entrepreneurs résilients et de la réussite startup",
        "Maîtriser le cadre S.T.A.R.T. pour réussir en affaires",
        "Planification business augmentée par l'IA et exécution lean startup",
        "Validation marché et tests clients avant le lancement",
        "Financement et bootstrap pour startups en phase précoce",
        "Stratégies de vente et marketing à fort impact dès le jour 1",
        "Construire une culture d'entreprise résiliente et une équipe scalable",
        "Stratégie finale de lancement et AI Startup Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "Failure-Proof Startup: How to Launch & Scale Without Risk",
      fr: "Startup anti-échec : lancer et scaler sans risque",
      es: null, pt: null
    }
  },
  {
    id: "bc-20",
    slug: "running-side-hustle-business",
    intro_video_url: "/__l5e/assets-v1/c3ea46eb-6b07-4a3b-80a9-788345085fc6/12e865971c2645b987ccd413c1af2cf4.mp4",
    track: "startup-launch",
    level: "beginner",
    duration_weeks: 4,
    price_eur: 129,
    price_xof: 85000,
    is_new: true,
    title: {
      en: "Running Your Side Hustle",
      fr: "Piloter son activité complémentaire",
      es: null, pt: null
    },
    card_tagline: {
      en: "Earn more, work less",
      fr: "Gagner plus, travailler moins",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered side hustle strategies, neuroscience-driven productivity, and high-impact income generation",
      fr: "Maîtriser les stratégies d'activité complémentaire augmentées par l'IA, la productivité appuyée par les neurosciences, et la génération de revenus à fort impact",
      es: null, pt: null
    },
    framework: "H.U.S.T.L.E.",
    framework_expansion: {
      en: "Habits, Uniqueness, Systems, Time Management, Leverage, Execution",
      fr: "Habitudes, Unicité, Systèmes, Gestion du temps, Effet de levier, Exécution",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Professionals looking to start and grow a side business",
        "Entrepreneurs and business owners monetizing passion projects",
        "Freelancers and gig workers looking for consistent income",
        "Employees transitioning from a job to full-time entrepreneurship"
      ],
      fr: [
        "Professionnels voulant lancer et développer une activité complémentaire",
        "Dirigeants monétisant leurs projets passion",
        "Freelances et travailleurs indépendants cherchant un revenu stable",
        "Salariés en transition vers l'entrepreneuriat à temps plein"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Motivation & Side Hustle Success",
        "Mastering the H.U.S.T.L.E. Framework for Growth",
        "Validating & Monetizing a Side Business Idea",
        "Time Management & Productivity for Side Hustlers",
        "Marketing & Customer Acquisition Without Ads",
        "Financial Planning & Scaling a Side Hustle",
        "Scaling a Side Hustle into a Full-Time Business",
        "Final Side Hustle Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la motivation et du succès en activité complémentaire",
        "Maîtriser le cadre H.U.S.T.L.E. pour la croissance",
        "Valider et monétiser une idée d'activité complémentaire",
        "Gestion du temps et productivité pour porteurs d'activité complémentaire",
        "Marketing et acquisition client sans publicité payante",
        "Planification financière et passage à l'échelle d'une activité complémentaire",
        "Transformer une activité complémentaire en activité principale",
        "Stratégie finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Side Hustles: The Future of Earning More While Working Less",
      fr: "Activités complémentaires augmentées par l'IA : gagner plus en travaillant moins",
      es: null, pt: null
    }
  },
  {
    id: "bc-11",
    slug: "business-model-innovation-value-proposition",
    intro_video_url: "/__l5e/assets-v1/3bdad1bc-0e31-498c-9d63-47ec6f3950ea/f55ee2283f5443799e1c7b75237cfbfb.mp4",
    track: "startup-launch",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 179,
    price_xof: 117500,
    is_new: true,
    title: {
      en: "Business Model Innovation",
      fr: "Innovation de business model",
      es: null, pt: null
    },
    card_tagline: {
      en: "Value proposition design",
      fr: "Design de proposition de valeur",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered strategy, neuroscience-driven business innovation, and high-impact value creation",
      fr: "Maîtriser la stratégie augmentée par l'IA, l'innovation business appuyée par les neurosciences, et la création de valeur à fort impact",
      es: null, pt: null
    },
    framework: "B.U.S.I.N.E.S.S.",
    framework_expansion: {
      en: "Breakthrough, Unique Value, Scalability, Innovation, Niche, Execution, Sustainability, Strategy",
      fr: "Percée, Valeur unique, Scalabilité, Innovation, Niche, Exécution, Durabilité, Stratégie",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and startup founders looking for disruptive business models",
        "Business owners seeking new revenue streams and scalability",
        "Innovators and product managers designing market-leading solutions",
        "Professionals looking to master business strategy and innovation"
      ],
      fr: [
        "Porteurs de projet cherchant des business models disruptifs",
        "Dirigeants cherchant de nouvelles sources de revenus et la scalabilité",
        "Innovateurs et chefs de produit concevant des solutions leaders sur leur marché",
        "Professionnels voulant maîtriser stratégie et innovation business"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Innovation & Business Strategy",
        "Mastering the B.U.S.I.N.E.S.S. Framework for Market Leadership",
        "Designing a Strong Value Proposition",
        "Revenue Models & Profitability Strategies",
        "AI & Digital Transformation in Business Models",
        "Customer Journey Mapping & Retention Strategies",
        "Scaling & Expanding Business Models",
        "Final Business Model & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de l'innovation et de la stratégie business",
        "Maîtriser le cadre B.U.S.I.N.E.S.S. pour devenir leader de marché",
        "Concevoir une proposition de valeur forte",
        "Modèles de revenus et stratégies de profitabilité",
        "IA et transformation digitale des business models",
        "Cartographie du parcours client et stratégies de rétention",
        "Scaler et étendre des business models",
        "Business model final et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Business Models: The Future of Value Creation",
      fr: "Business models augmentés par l'IA : le futur de la création de valeur",
      es: null, pt: null
    }
  },
  {
    id: "bc-19",
    slug: "preventing-bankruptcy-early-days",
    intro_video_url: "/__l5e/assets-v1/48b0bb24-61c9-462b-932c-35851c7ac6d9/08411063625d4baa9b3604e6fd546a4a.mp4",
    track: "startup-launch",
    level: "beginner",
    duration_weeks: 4,
    price_eur: 149,
    price_xof: 97800,
    is_new: true,
    title: {
      en: "Preventing Bankruptcy Early",
      fr: "Prévenir la faillite dès les premiers jours",
      es: null, pt: null
    },
    card_tagline: {
      en: "Cash flow & financial resilience",
      fr: "Trésorerie et résilience financière",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered financial resilience, neuroscience-driven risk management, and high-impact business sustainability strategies",
      fr: "Maîtriser la résilience financière augmentée par l'IA, la gestion des risques appuyée par les neurosciences, et les stratégies de durabilité à fort impact",
      es: null, pt: null
    },
    framework: "S.A.V.E.",
    framework_expansion: {
      en: "Strategy, Awareness, Viability, Execution",
      fr: "Stratégie, Conscience, Viabilité, Exécution",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and startup founders seeking financial stability",
        "Small business owners managing cash flow and debt risk",
        "Freelancers and self-employed professionals avoiding business failure",
        "Business leaders looking to build long-term financial resilience"
      ],
      fr: [
        "Porteurs de projet cherchant la stabilité financière",
        "Dirigeants de PME gérant trésorerie et risque de dette",
        "Freelances et indépendants évitant l'échec commercial",
        "Dirigeants construisant une résilience financière de long terme"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Financial Decision-Making & Risk Tolerance",
        "Mastering the S.A.V.E. Framework for Business Resilience",
        "Cash Flow Management & Expense Optimization",
        "Avoiding Debt Traps & Managing Business Credit Smartly",
        "Revenue Diversification & Financial Resilience",
        "Business Model Adjustments & Pivoting for Survival",
        "Crisis Planning & Business Continuity Strategy",
        "Final Bankruptcy Prevention Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la décision financière et de la tolérance au risque",
        "Maîtriser le cadre S.A.V.E. pour la résilience business",
        "Gestion de trésorerie et optimisation des dépenses",
        "Éviter les pièges de la dette et gérer le crédit intelligemment",
        "Diversification des revenus et résilience financière",
        "Ajustements du business model et pivots de survie",
        "Plan de crise et stratégie de continuité d'activité",
        "Stratégie finale anti-faillite et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Financial Resilience: How to Prevent Bankruptcy & Build a Sustainable Business",
      fr: "Résilience financière augmentée par l'IA : prévenir la faillite et construire une entreprise durable",
      es: null, pt: null
    }
  },

  // ============================================================================
  // TRACK 2: GROWTH & SCALE (first 4 of 6 — remaining 2 in Sprint 2)
  // ============================================================================

  {
    id: "bc-18",
    slug: "growing-scaling-your-business",
    intro_video_url: "/__l5e/assets-v1/3ae56afe-dba5-4037-8a89-3f5d83aa81b8/4006961a53834a1db5779bfcc38a9858.mp4",
    track: "growth-scale",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: false,
    status: "upcoming",
    next_cohort_at: "2026-09-15",
    title: {
      en: "Growing & Scaling Your Business",
      fr: "Faire croître et scaler son entreprise",
      es: null, pt: null
    },
    card_tagline: {
      en: "Financial planning, automation, expansion",
      fr: "Planification financière, automatisation, expansion",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered scaling strategies, neuroscience-driven growth tactics, and high-impact business expansion techniques",
      fr: "Maîtriser les stratégies de scale augmentées par l'IA, les tactiques de croissance appuyées par les neurosciences, et les techniques d'expansion à fort impact",
      es: null, pt: null
    },
    framework: "G.R.O.W.",
    framework_expansion: {
      en: "Goal Setting, Revenue Expansion, Operational Efficiency, Winning Strategy",
      fr: "Fixation d'objectifs, Expansion des revenus, Efficience opérationnelle, Stratégie gagnante",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and business owners ready to scale",
        "Startup founders looking for sustainable growth strategies",
        "Business development and strategy leaders seeking market expansion",
        "Professionals transitioning from small-scale to large-scale operations"
      ],
      fr: [
        "Dirigeants prêts à scaler",
        "Fondateurs de startup cherchant des stratégies de croissance durable",
        "Responsables business development et stratégie cherchant l'expansion",
        "Professionnels en transition d'opérations petites vers grandes échelles"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Business Growth & Scaling Mindset",
        "Mastering the G.R.O.W. Framework for Business Scaling",
        "Financial Planning & Capital Allocation for Scaling",
        "AI & Automation in Scaling Business Operations",
        "Sales & Marketing Strategies for Scaling Growth",
        "Hiring & Leadership Development for Scaling Businesses",
        "Market Expansion & Globalization Strategies",
        "Final Scaling Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la croissance et de l'état d'esprit de scale",
        "Maîtriser le cadre G.R.O.W. pour scaler son entreprise",
        "Planification financière et allocation de capital pour le scale",
        "IA et automatisation dans le scale des opérations",
        "Stratégies de ventes et marketing pour une croissance scalable",
        "Recrutement et développement du leadership pour entreprises en scale",
        "Expansion marché et stratégies de globalisation",
        "Stratégie finale de scale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Scaling: How to Grow & Expand Your Business Sustainably",
      fr: "Scale augmenté par l'IA : faire croître et étendre son entreprise durablement",
      es: null, pt: null
    }
  },
  {
    id: "bc-12",
    slug: "scaling-up-growth-small-business",
    intro_video_url: "/__l5e/assets-v1/bcf9232d-acf4-4430-9fa6-9040bcf9ba01/debfb415db1c496b80293857d951d73d.mp4",
    track: "growth-scale",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: {
      en: "Scaling Up",
      fr: "Passer à l'échelle",
      es: null, pt: null
    },
    card_tagline: {
      en: "Growth strategies for small business",
      fr: "Stratégies de croissance pour PME",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered growth tactics, neuroscience-driven business expansion, and scalable revenue models",
      fr: "Maîtriser les tactiques de croissance augmentées par l'IA, l'expansion business appuyée par les neurosciences, et les modèles de revenus scalables",
      es: null, pt: null
    },
    framework: "S.C.A.L.E.",
    framework_expansion: {
      en: "Strategy, Capital, Automation, Leverage, Expansion",
      fr: "Stratégie, Capital, Automatisation, Effet de levier, Expansion",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and small business owners looking to scale",
        "Startup founders preparing for rapid expansion",
        "Business development and growth strategy professionals",
        "Anyone seeking data-driven strategies to scale sustainably"
      ],
      fr: [
        "Dirigeants de PME voulant scaler",
        "Fondateurs de startup préparant une expansion rapide",
        "Professionnels du business development et de la stratégie de croissance",
        "Toute personne cherchant des stratégies data-driven pour scaler durablement"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Scaling & Growth Mindset",
        "Mastering the S.C.A.L.E. Framework for Business Growth",
        "Financial Planning & Capital Allocation for Scaling",
        "AI & Automation in Scaling Operations",
        "Marketing & Sales Strategies for Business Growth",
        "Hiring, Talent Development & Leadership for Scaling",
        "Market Expansion & Globalization Strategies",
        "Final Scaling Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences du scale et de l'état d'esprit de croissance",
        "Maîtriser le cadre S.C.A.L.E. pour la croissance business",
        "Planification financière et allocation de capital pour scaler",
        "IA et automatisation dans le scale des opérations",
        "Stratégies marketing et ventes pour la croissance business",
        "Recrutement, développement des talents et leadership pour scaler",
        "Expansion marché et stratégies de globalisation",
        "Stratégie finale de scale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Scaling Strategies: The Future of Business Growth",
      fr: "Stratégies de scale augmentées par l'IA : le futur de la croissance business",
      es: null, pt: null
    }
  },
  {
    id: "bc-06",
    slug: "operational-excellence-process-improvement",
    intro_video_url: "/__l5e/assets-v1/71ded356-e849-4b17-b573-236b96374de0/a01fc1930ea74f6fac8da6b2048ed925.mp4",
    track: "growth-scale",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: {
      en: "Operational Excellence",
      fr: "Excellence opérationnelle",
      es: null, pt: null
    },
    card_tagline: {
      en: "Lean processes & AI efficiency",
      fr: "Processus lean et efficience IA",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered efficiency, lean processes, and neuroscience-backed performance strategies",
      fr: "Maîtriser l'efficience augmentée par l'IA, les processus lean, et les stratégies de performance appuyées par les neurosciences",
      es: null, pt: null
    },
    framework: "O.P.T.I.M.I.Z.E.",
    framework_expansion: {
      en: "Organize, Prioritize, Track, Improve, Measure, Innovate, Zero Waste, Execute",
      fr: "Organiser, Prioriser, Suivre, Améliorer, Mesurer, Innover, Zéro gaspillage, Exécuter",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and business owners seeking efficiency",
        "Operations managers and process improvement leaders",
        "Startup founders looking to scale efficiently",
        "Professionals managing teams and workflows"
      ],
      fr: [
        "Dirigeants cherchant l'efficience",
        "Responsables opérations et chefs de projet d'amélioration continue",
        "Fondateurs de startup cherchant à scaler efficacement",
        "Professionnels gérant équipes et flux de travail"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Productivity & Efficiency",
        "Mastering the O.P.T.I.M.I.Z.E. Framework",
        "Lean & Agile Process Improvement Strategies",
        "Automation & AI-Driven Business Efficiency",
        "Building High-Performance Teams for Operational Success",
        "Data-Driven Decision Making & Performance Metrics",
        "Crisis Management & Business Continuity Planning",
        "Final Operational Excellence Plan & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la productivité et de l'efficience",
        "Maîtriser le cadre O.P.T.I.M.I.Z.E.",
        "Stratégies d'amélioration de processus lean et agiles",
        "Automatisation et efficience business augmentée par l'IA",
        "Construire des équipes haute performance pour le succès opérationnel",
        "Décisions data-driven et indicateurs de performance",
        "Gestion de crise et planification de continuité d'activité",
        "Plan final d'excellence opérationnelle et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Driven Efficiency: The Future of Operational Excellence",
      fr: "Efficience pilotée par l'IA : le futur de l'excellence opérationnelle",
      es: null, pt: null
    }
  },
  {
    id: "bc-03",
    slug: "strategic-planning-execution-mastery",
    intro_video_url: "/__l5e/assets-v1/bdc1aee8-abc5-4694-9756-4cfad0403874/17edda8b8433495c9948c913bf75a5ed.mp4",
    track: "growth-scale",
    level: "intermediate",
    duration_weeks: 6,
    price_eur: 249,
    price_xof: 163400,
    is_new: true,
    title: {
      en: "Strategic Planning & Execution",
      fr: "Planification et exécution stratégique",
      es: null, pt: null
    },
    card_tagline: {
      en: "S.T.R.A.T.E.G.Y. framework mastery",
      fr: "Maîtrise du cadre S.T.R.A.T.E.G.Y.",
      es: null, pt: null
    },
    subtitle: {
      en: "Master the neuroscience and AI-powered strategies for business growth and success",
      fr: "Maîtriser les neurosciences et les stratégies augmentées par l'IA pour la croissance et la réussite",
      es: null, pt: null
    },
    framework: "S.T.R.A.T.E.G.Y.",
    framework_expansion: {
      en: "Structure, Tactics, Resources, Action, Timing, Execution, Growth, Yield",
      fr: "Structure, Tactiques, Ressources, Action, Timing, Exécution, Croissance, Rendement",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Startup founders and entrepreneurs",
        "Business owners scaling their operations",
        "Professionals in leadership roles",
        "Anyone seeking a proven strategy execution framework"
      ],
      fr: [
        "Fondateurs de startup et entrepreneurs",
        "Dirigeants scalant leurs opérations",
        "Professionnels en rôle de leadership",
        "Toute personne cherchant un cadre éprouvé d'exécution stratégique"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Strategic Thinking & Execution",
        "Mastering the S.T.R.A.T.E.G.Y. Framework for Business Success",
        "Strategic Planning & Goal Setting for Long-Term Growth",
        "Resource Allocation & Operational Efficiency",
        "AI & Data-Driven Decision Making in Strategy",
        "Execution Discipline & Performance Tracking",
        "Adapting Strategy to Market Disruptions",
        "Final Strategic Plan & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la pensée stratégique et de l'exécution",
        "Maîtriser le cadre S.T.R.A.T.E.G.Y. pour réussir en affaires",
        "Planification stratégique et fixation d'objectifs pour la croissance long terme",
        "Allocation des ressources et efficience opérationnelle",
        "Décisions data-driven et augmentées par l'IA en stratégie",
        "Discipline d'exécution et suivi de performance",
        "Adapter la stratégie aux disruptions de marché",
        "Plan stratégique final et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Strategy: The Future of Business Planning & Execution",
      fr: "Stratégie augmentée par l'IA : le futur de la planification et de l'exécution business",
      es: null, pt: null
    }
  },

  // ============================================================================
  // TRACK 2: GROWTH & SCALE (remainder — 2 bootcamps)
  // ============================================================================

  {
    id: "bc-30",
    slug: "strategic-success-planning-execution-masterclass",
    intro_video_url: "/__l5e/assets-v1/9a9e460d-e332-41f2-8ad4-8d354097963d/5a92652a16524b3fa4870800e582f4de.mp4",
    track: "growth-scale",
    level: "advanced",
    duration_weeks: 6,
    price_eur: 299,
    price_xof: 196200,
    is_new: true,
    title: {
      en: "Strategic Success Masterclass",
      fr: "Masterclass réussite stratégique",
      es: null, pt: null
    },
    card_tagline: {
      en: "Planning & execution at scale",
      fr: "Planification et exécution à grande échelle",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered business strategy, neuroscience-driven goal execution, and high-impact leadership planning",
      fr: "Maîtriser la stratégie business augmentée par l'IA, l'exécution des objectifs appuyée par les neurosciences, et la planification leadership à fort impact",
      es: null, pt: null
    },
    framework: "S.T.R.A.T.E.G.I.C.",
    framework_expansion: {
      en: "Structure, Timing, Resources, Alignment, Tactics, Execution, Goals, Iteration, Consistency",
      fr: "Structure, Timing, Ressources, Alignement, Tactiques, Exécution, Objectifs, Itération, Constance",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and business owners needing structured growth plans",
        "Startup founders seeking scalable execution strategies",
        "Corporate leaders and executives driving strategic initiatives",
        "Consultants and professionals improving business decision-making"
      ],
      fr: [
        "Dirigeants ayant besoin de plans de croissance structurés",
        "Porteurs de projet cherchant des stratégies d'exécution scalables",
        "Dirigeants et cadres pilotant des initiatives stratégiques",
        "Consultants et professionnels améliorant la décision business"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Strategic Thinking & Leadership Success",
        "Mastering the S.T.R.A.T.E.G.I.C. Framework for Business Growth",
        "Data-Driven Strategic Planning & Goal Setting",
        "Execution Mastery & High-Performance Leadership",
        "AI & Automation in Business Strategy",
        "Risk Management & Crisis-Proof Business Planning",
        "Change Management & Business Adaptability",
        "Final Strategic Business Plan & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la pensée stratégique et du succès en leadership",
        "Maîtriser le cadre S.T.R.A.T.E.G.I.C. pour la croissance business",
        "Planification stratégique data-driven et fixation d'objectifs",
        "Maîtrise de l'exécution et leadership haute performance",
        "IA et automatisation dans la stratégie business",
        "Gestion des risques et planification anti-crise",
        "Gestion du changement et adaptabilité business",
        "Plan stratégique business final et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Strategic Success: The Future of Business Planning & Execution",
      fr: "Réussite stratégique augmentée par l'IA : le futur de la planification et de l'exécution",
      es: null, pt: null
    }
  },
  {
    id: "bc-24",
    slug: "disruptive-innovation-exponential-growth",
    intro_video_url: "/__l5e/assets-v1/5a23378f-9d00-419e-a2dc-35dde332beff/1819e55dac8948af913a40e5cc7da281.mp4",
    track: "growth-scale",
    level: "advanced",
    duration_weeks: 5,
    price_eur: 249,
    price_xof: 163400,
    is_new: true,
    title: {
      en: "Disruptive Innovation",
      fr: "Innovation disruptive",
      es: null, pt: null
    },
    card_tagline: {
      en: "Fueling exponential growth",
      fr: "Alimenter la croissance exponentielle",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered innovation, neuroscience-driven creativity, and high-impact disruption strategies",
      fr: "Maîtriser l'innovation augmentée par l'IA, la créativité appuyée par les neurosciences, et les stratégies de disruption à fort impact",
      es: null, pt: null
    },
    framework: "D.I.S.R.U.P.T.",
    framework_expansion: {
      en: "Discover, Identify, Strategize, Reinvent, Utilize, Prototype, Transform",
      fr: "Découvrir, Identifier, Strategiser, Réinventer, Utiliser, Prototyper, Transformer",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and startup founders seeking breakthrough innovation",
        "Business leaders driving transformation in established companies",
        "Product managers and innovators creating new market categories",
        "Investors and strategists tracking disruptive opportunities"
      ],
      fr: [
        "Porteurs de projet et dirigeants de startup cherchant l'innovation de rupture",
        "Dirigeants pilotant la transformation d'entreprises établies",
        "Chefs de produit et innovateurs créant de nouvelles catégories de marché",
        "Investisseurs et stratèges suivant les opportunités disruptives"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Innovation & Disruptive Thinking",
        "Mastering the D.I.S.R.U.P.T. Framework for Market Leadership",
        "Identifying Disruption Opportunities in Any Industry",
        "AI & Emerging Tech as Disruption Accelerators",
        "Building a Disruptive Business Model",
        "Prototyping, Testing & Iterating at Speed",
        "Scaling a Disruptive Business",
        "Final Disruption Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de l'innovation et de la pensée disruptive",
        "Maîtriser le cadre D.I.S.R.U.P.T. pour devenir leader de marché",
        "Identifier les opportunités de disruption dans toute industrie",
        "IA et tech émergentes comme accélérateurs de disruption",
        "Construire un business model disruptif",
        "Prototyper, tester et itérer rapidement",
        "Scaler une entreprise disruptive",
        "Stratégie finale de disruption et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Disruption: The Future of Exponential Business Growth",
      fr: "Disruption augmentée par l'IA : le futur de la croissance exponentielle",
      es: null, pt: null
    }
  },

  // ============================================================================
  // TRACK 3: LEADERSHIP & MINDSET (8 bootcamps)
  // ============================================================================

  {
    id: "bc-01",
    slug: "entrepreneurial-mindset-mastery",
    intro_video_url: "/__l5e/assets-v1/48e1645c-02f0-4de6-9aaa-8eb6f8a919c3/a6fe938584324f949c5829fcd0d4e125.mp4",
    track: "leadership-mindset",
    level: "beginner",
    duration_weeks: 4,
    price_eur: 149,
    price_xof: 97800,
    is_new: true,
    title: {
      en: "Entrepreneurial Mindset Mastery",
      fr: "Maîtrise de l'état d'esprit entrepreneurial",
      es: null, pt: null
    },
    card_tagline: {
      en: "Psychology, resilience, risk-taking",
      fr: "Psychologie, résilience, prise de risque",
      es: null, pt: null
    },
    subtitle: {
      en: "Master the psychology, resilience, and risk-taking required to build a successful business",
      fr: "Maîtriser la psychologie, la résilience et la prise de risque nécessaires pour construire une entreprise qui réussit",
      es: null, pt: null
    },
    framework: "G.R.I.T.",
    framework_expansion: {
      en: "Growth, Resilience, Innovation, Tenacity",
      fr: "Croissance, Résilience, Innovation, Ténacité",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Aspiring and early-stage entrepreneurs",
        "Business owners navigating uncertainty",
        "Professionals developing entrepreneurial thinking",
        "Anyone building mental resilience for business"
      ],
      fr: [
        "Aspirants et entrepreneurs en phase précoce",
        "Dirigeants naviguant dans l'incertitude",
        "Professionnels développant la pensée entrepreneuriale",
        "Toute personne construisant sa résilience mentale pour les affaires"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of the Entrepreneurial Brain",
        "Mastering the G.R.I.T. Framework for Success",
        "Risk Intelligence & the C.A.R.E. Model",
        "Overcoming Self-Sabotage (A.R.M.O.R. Model)",
        "Strategic Decision-Making (D.E.C.I.D.E. Model)",
        "Building an Unstoppable Growth Mindset",
        "Resilience, Focus & Long-Term Mastery",
        "Final Mindset Strategy & AI Validator"
      ],
      fr: [
        "Les neurosciences du cerveau entrepreneurial",
        "Maîtriser le cadre G.R.I.T. pour réussir",
        "Intelligence du risque et modèle C.A.R.E.",
        "Dépasser l'auto-sabotage (modèle A.R.M.O.R.)",
        "Prise de décision stratégique (modèle D.E.C.I.D.E.)",
        "Construire un état d'esprit de croissance inarrêtable",
        "Résilience, focus et maîtrise de long terme",
        "Stratégie finale d'état d'esprit et AI Validator"
      ],
      es: null, pt: null
    },
    ebook_title: { en: null, fr: null, es: null, pt: null }
  },
  {
    id: "bc-02",
    slug: "leadership-excellence-emerging-leaders",
    intro_video_url: "/__l5e/assets-v1/1e4a82b2-f55e-4c1b-bbe9-0fa61fe7201b/f46d9538a4184666af0a5ce76a52b891.mp4",
    track: "leadership-mindset",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: false,
    title: {
      en: "Leadership for Emerging Leaders",
      fr: "Leadership pour dirigeants émergents",
      es: null, pt: null
    },
    card_tagline: {
      en: "Emotional intelligence & team building",
      fr: "Intelligence émotionnelle et construction d'équipe",
      es: null, pt: null
    },
    subtitle: {
      en: "Build high-performance teams through neuroscience-backed leadership and AI-enhanced decision-making",
      fr: "Construire des équipes haute performance via un leadership appuyé par les neurosciences et une décision augmentée par l'IA",
      es: null, pt: null
    },
    framework: "L.E.A.D.",
    framework_expansion: {
      en: "Listen, Empower, Adapt, Deliver",
      fr: "Écouter, Responsabiliser, Adapter, Livrer",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Emerging leaders and first-time managers",
        "Startup founders leading their first teams",
        "Mid-level executives developing leadership capacity",
        "High-potential professionals targeting leadership roles"
      ],
      fr: [
        "Dirigeants émergents et managers débutants",
        "Porteurs de projet dirigeant leurs premières équipes",
        "Cadres intermédiaires développant leur capacité de leadership",
        "Professionnels à haut potentiel visant des postes de direction"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Leadership & Emotional Intelligence",
        "Mastering the L.E.A.D. Framework",
        "Strategic Communication & the N.E.U.R.O. Model",
        "Building & Leading High-Performance Teams",
        "Decision-Making Under Pressure",
        "Change Leadership & Organizational Agility",
        "AI-Powered Leadership & Data-Driven Decisions",
        "Final Leadership Plan & AI Validator"
      ],
      fr: [
        "Les neurosciences du leadership et de l'intelligence émotionnelle",
        "Maîtriser le cadre L.E.A.D.",
        "Communication stratégique et modèle N.E.U.R.O.",
        "Construire et diriger des équipes haute performance",
        "Prise de décision sous pression",
        "Leadership du changement et agilité organisationnelle",
        "Leadership augmenté par l'IA et décisions data-driven",
        "Plan de leadership final et AI Validator"
      ],
      es: null, pt: null
    },
    ebook_title: { en: null, fr: null, es: null, pt: null }
  },
  {
    id: "bc-15",
    slug: "emotional-intelligence-leadership",
    intro_video_url: "/__l5e/assets-v1/28f54744-6e61-435a-a759-01779ca8eb52/3015b04c6b8b48ea88cb6c93a744ee84.mp4",
    track: "leadership-mindset",
    level: "intermediate",
    duration_weeks: 4,
    price_eur: 179,
    price_xof: 117500,
    is_new: true,
    title: {
      en: "Emotional Intelligence & Leadership",
      fr: "Intelligence émotionnelle et leadership",
      es: null, pt: null
    },
    card_tagline: {
      en: "Empathy, influence, trust",
      fr: "Empathie, influence, confiance",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered leadership, neuroscience-driven emotional intelligence, and high-impact influence strategies",
      fr: "Maîtriser le leadership augmenté par l'IA, l'intelligence émotionnelle appuyée par les neurosciences, et les stratégies d'influence à fort impact",
      es: null, pt: null
    },
    framework: "E.M.O.T.I.O.N.",
    framework_expansion: {
      en: "Empathy, Mindfulness, Observation, Trust, Influence, Optimism, Nurture",
      fr: "Empathie, Pleine conscience, Observation, Confiance, Influence, Optimisme, Nurture",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and business leaders",
        "Managers and team leaders developing emotional skills",
        "Professionals in high-stakes interpersonal roles",
        "Anyone building influence through emotional mastery"
      ],
      fr: [
        "Porteurs de projet et dirigeants",
        "Managers et chefs d'équipe développant leurs compétences émotionnelles",
        "Professionnels dans des rôles interpersonnels à forte exigence",
        "Toute personne construisant son influence via la maîtrise émotionnelle"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Emotional Intelligence & Leadership",
        "Mastering the E.M.O.T.I.O.N. Framework",
        "Self-Awareness & Emotional Regulation",
        "Empathy, Active Listening & Trust-Building",
        "Influencing Others & Strategic Communication",
        "Conflict Resolution & Difficult Conversations",
        "Emotional Intelligence in High-Stakes Decisions",
        "Final EI Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de l'intelligence émotionnelle et du leadership",
        "Maîtriser le cadre E.M.O.T.I.O.N.",
        "Conscience de soi et régulation émotionnelle",
        "Empathie, écoute active et construction de la confiance",
        "Influencer les autres et communication stratégique",
        "Résolution de conflits et conversations difficiles",
        "Intelligence émotionnelle dans les décisions à fort enjeu",
        "Stratégie finale IE et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Emotional Intelligence: The Future of Leadership & Influence",
      fr: "Intelligence émotionnelle augmentée par l'IA : le futur du leadership et de l'influence",
      es: null, pt: null
    }
  },
  {
    id: "bc-09",
    slug: "building-leading-high-performance-teams",
    intro_video_url: "/__l5e/assets-v1/ac91a29c-1dfd-40dd-ae2e-14a40e7571ed/0b4766067f98456e97e36ba73ca906bf.mp4",
    track: "leadership-mindset",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: {
      en: "High-Performance Teams",
      fr: "Équipes haute performance",
      es: null, pt: null
    },
    card_tagline: {
      en: "Build, lead, retain top talent",
      fr: "Construire, diriger, fidéliser les meilleurs",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered team building, neuroscience-driven leadership, and high-impact collaboration strategies",
      fr: "Maîtriser la construction d'équipe augmentée par l'IA, le leadership appuyé par les neurosciences, et les stratégies de collaboration à fort impact",
      es: null, pt: null
    },
    framework: "T.E.A.M.",
    framework_expansion: {
      en: "Trust, Engagement, Accountability, Motivation",
      fr: "Confiance, Engagement, Responsabilité, Motivation",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and business owners building their first teams",
        "Managers and team leaders driving performance",
        "HR professionals and talent development specialists",
        "Leaders scaling teams through rapid growth"
      ],
      fr: [
        "Porteurs de projet et dirigeants construisant leurs premières équipes",
        "Managers et chefs d'équipe pilotant la performance",
        "Professionnels RH et spécialistes du développement des talents",
        "Dirigeants scalant leurs équipes en phase de croissance rapide"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Team Performance & Group Psychology",
        "Mastering the T.E.A.M. Framework for High Performance",
        "Hiring & Onboarding for Team Excellence",
        "Building Trust & Psychological Safety",
        "Motivation, Engagement & Team Culture",
        "Accountability Systems & Performance Management",
        "Conflict Resolution & Team Dynamics",
        "Final Team Building Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la performance d'équipe et de la psychologie de groupe",
        "Maîtriser le cadre T.E.A.M. pour la haute performance",
        "Recrutement et onboarding pour l'excellence d'équipe",
        "Construire la confiance et la sécurité psychologique",
        "Motivation, engagement et culture d'équipe",
        "Systèmes de responsabilité et gestion de la performance",
        "Résolution de conflits et dynamiques d'équipe",
        "Stratégie finale de construction d'équipe et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Team Leadership: Building & Leading High-Performance Teams",
      fr: "Leadership d'équipe augmenté par l'IA : construire et diriger des équipes haute performance",
      es: null, pt: null
    }
  },
  {
    id: "bc-26",
    slug: "change-leadership-transformation",
    intro_video_url: "/__l5e/assets-v1/1c5b9485-105a-43dc-a46f-928fd8b8d835/1267fb3b3c364ff2b07ece61c05fb1fa.mp4",
    track: "leadership-mindset",
    level: "advanced",
    duration_weeks: 5,
    price_eur: 229,
    price_xof: 150300,
    is_new: true,
    title: {
      en: "Change Leadership",
      fr: "Leadership du changement",
      es: null, pt: null
    },
    card_tagline: {
      en: "Drive organizational transformation",
      fr: "Piloter la transformation organisationnelle",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered change management, neuroscience-driven transformation, and high-impact organizational leadership",
      fr: "Maîtriser le change management augmenté par l'IA, la transformation appuyée par les neurosciences, et le leadership organisationnel à fort impact",
      es: null, pt: null
    },
    framework: "C.H.A.N.G.E.",
    framework_expansion: {
      en: "Clarity, Human Focus, Agility, Nurturing, Growth, Execution",
      fr: "Clarté, Focus humain, Agilité, Nurture, Croissance, Exécution",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Business leaders driving organizational transformation",
        "Executives and senior managers leading change initiatives",
        "Entrepreneurs scaling and restructuring their businesses",
        "HR and organizational development professionals"
      ],
      fr: [
        "Dirigeants pilotant la transformation organisationnelle",
        "Cadres dirigeants et seniors conduisant des initiatives de changement",
        "Porteurs de projet scalant et restructurant leurs entreprises",
        "Professionnels RH et du développement organisationnel"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Change & Organizational Psychology",
        "Mastering the C.H.A.N.G.E. Framework for Transformation",
        "Diagnosing Readiness for Change",
        "Communication Strategies for Change Leadership",
        "Managing Resistance & Building Buy-In",
        "Executing Change at Scale",
        "Sustaining Change & Embedding New Behaviors",
        "Final Change Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences du changement et de la psychologie organisationnelle",
        "Maîtriser le cadre C.H.A.N.G.E. pour la transformation",
        "Diagnostiquer la maturité face au changement",
        "Stratégies de communication pour le leadership du changement",
        "Gérer la résistance et construire l'adhésion",
        "Exécuter le changement à grande échelle",
        "Pérenniser le changement et ancrer les nouveaux comportements",
        "Stratégie finale de changement et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Change Leadership: The Future of Organizational Transformation",
      fr: "Leadership du changement augmenté par l'IA : le futur de la transformation organisationnelle",
      es: null, pt: null
    }
  },
  {
    id: "bc-23",
    slug: "seven-habits-successful-entrepreneurs",
    intro_video_url: "/__l5e/assets-v1/1691c726-57c5-414c-b404-a597170d4115/e8608f76a287409893e5a4ce02da4645.mp4",
    track: "leadership-mindset",
    level: "intermediate",
    duration_weeks: 4,
    price_eur: 179,
    price_xof: 117500,
    is_new: true,
    title: {
      en: "Seven Habits of Successful Entrepreneurs",
      fr: "Sept habitudes des entrepreneurs à succès",
      es: null, pt: null
    },
    card_tagline: {
      en: "Productive routines for founders",
      fr: "Routines productives pour dirigeants",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered productivity, neuroscience-driven habit formation, and high-impact entrepreneurial routines",
      fr: "Maîtriser la productivité augmentée par l'IA, la formation d'habitudes appuyée par les neurosciences, et les routines entrepreneuriales à fort impact",
      es: null, pt: null
    },
    framework: "H.A.B.I.T.S.",
    framework_expansion: {
      en: "High Performance, Action, Balance, Innovation, Time Mastery, Self-Discipline",
      fr: "Haute performance, Action, Équilibre, Innovation, Maîtrise du temps, Autodiscipline",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and business owners building productive routines",
        "High-performing professionals optimizing their habits",
        "Startup founders managing intense workloads",
        "Anyone seeking to build sustainable success habits"
      ],
      fr: [
        "Porteurs de projet construisant des routines productives",
        "Professionnels haute performance optimisant leurs habitudes",
        "Porteurs de projet startup gérant des charges de travail intenses",
        "Toute personne construisant des habitudes de succès durables"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Habit Formation & Behavioral Change",
        "Mastering the H.A.B.I.T.S. Framework for Success",
        "Morning Routines & High-Performance Rituals",
        "Deep Work, Focus & Productivity Systems",
        "Goal-Setting & Daily Execution Habits",
        "Rest, Recovery & Sustainable Energy Management",
        "Continuous Learning & Self-Improvement",
        "Final Habits Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la formation d'habitudes et du changement comportemental",
        "Maîtriser le cadre H.A.B.I.T.S. pour le succès",
        "Routines matinales et rituels haute performance",
        "Deep work, focus et systèmes de productivité",
        "Fixation d'objectifs et habitudes d'exécution quotidienne",
        "Repos, récupération et gestion durable de l'énergie",
        "Apprentissage continu et auto-amélioration",
        "Stratégie finale d'habitudes et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Habits: The Seven Routines of Highly Successful Entrepreneurs",
      fr: "Habitudes augmentées par l'IA : les sept routines des entrepreneurs à succès",
      es: null, pt: null
    }
  },
  {
    id: "bc-44",
    slug: "10x-mindset-billionaire-entrepreneurs",
    intro_video_url: "/__l5e/assets-v1/5754f948-6cb4-4cd4-bd37-687b68474767/499f728d60d0407d84796871d67fd3fa.mp4",
    track: "leadership-mindset",
    level: "advanced",
    duration_weeks: 5,
    price_eur: 249,
    price_xof: 163400,
    is_new: true,
    title: {
      en: "The 10X Mindset",
      fr: "L'état d'esprit 10X",
      es: null, pt: null
    },
    card_tagline: {
      en: "Think like billionaire founders",
      fr: "Penser comme les dirigeants milliardaires",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered exponential thinking, neuroscience-driven ambition, and high-impact wealth-building mindset",
      fr: "Maîtriser la pensée exponentielle augmentée par l'IA, l'ambition appuyée par les neurosciences, et l'état d'esprit de construction de richesse à fort impact",
      es: null, pt: null
    },
    framework: "E.X.P.O.N.E.N.T.I.A.L.",
    framework_expansion: {
      en: "Execution, X-Factor, Planning, Optimization, Network, Energy, Never-Satisfied, Tenacity, Innovation, Audacity, Leverage",
      fr: "Exécution, Facteur X, Planification, Optimisation, Réseau, Énergie, Jamais satisfait, Ténacité, Innovation, Audace, Effet de levier",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Ambitious entrepreneurs aiming for breakthrough growth",
        "Founders seeking to scale 10X instead of incrementally",
        "Business leaders with exponential ambitions",
        "High-performers unlocking their maximum potential"
      ],
      fr: [
        "Entrepreneurs ambitieux visant une croissance de rupture",
        "Porteurs de projet cherchant à scaler x10 plutôt que de manière incrémentale",
        "Dirigeants aux ambitions exponentielles",
        "Hauts performeurs libérant leur potentiel maximal"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Ambition & Exponential Thinking",
        "Mastering the E.X.P.O.N.E.N.T.I.A.L. Framework",
        "From Linear to Exponential: Reframing Your Business",
        "Audacity, Bold Goals & High-Stakes Execution",
        "The Billionaire Habits of Daily Execution",
        "Network, Leverage & Wealth-Building Strategies",
        "Innovation, Risk-Taking & Disruption",
        "Final 10X Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de l'ambition et de la pensée exponentielle",
        "Maîtriser le cadre E.X.P.O.N.E.N.T.I.A.L.",
        "Du linéaire à l'exponentiel : reframer son business",
        "Audace, objectifs bold et exécution à haut risque",
        "Les habitudes quotidiennes des milliardaires",
        "Réseau, effet de levier et stratégies de construction de richesse",
        "Innovation, prise de risque et disruption",
        "Stratégie finale 10X et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered 10X Thinking: The Mindset of Billionaire Entrepreneurs",
      fr: "Pensée 10X augmentée par l'IA : l'état d'esprit des entrepreneurs milliardaires",
      es: null, pt: null
    }
  },
  {
    id: "bc-16",
    slug: "influence-leadership-young-female-entrepreneurs",
    intro_video_url: "/__l5e/assets-v1/2e3938f7-a67d-4769-a580-98d241214746/f65b016eeeae44398d737ed42654dcfa.mp4",
    track: "leadership-mindset",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 179,
    price_xof: 117500,
    is_new: true,
    title: {
      en: "Influence for Young Female Entrepreneurs",
      fr: "Influence pour les jeunes femmes entrepreneures",
      es: null, pt: null
    },
    card_tagline: {
      en: "Leadership and network building",
      fr: "Leadership et construction de réseau",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered leadership, neuroscience-driven confidence, and high-impact influence strategies for women in business",
      fr: "Maîtriser le leadership augmenté par l'IA, la confiance appuyée par les neurosciences, et les stratégies d'influence à fort impact pour les femmes en affaires",
      es: null, pt: null
    },
    framework: "W.O.M.E.N.",
    framework_expansion: {
      en: "Wisdom, Opportunities, Mindset, Empowerment, Networking",
      fr: "Sagesse, Opportunités, État d'esprit, Responsabilisation, Networking",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Young female entrepreneurs building their businesses",
        "Women leaders navigating male-dominated industries",
        "Female professionals targeting leadership roles",
        "Women founders growing their influence and network"
      ],
      fr: [
        "Jeunes femmes entrepreneures construisant leurs entreprises",
        "Femmes dirigeantes naviguant dans des industries à dominance masculine",
        "Femmes professionnelles visant des postes de direction",
        "Femmes porteuses de projet développant leur influence et leur réseau"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Confidence & Female Leadership",
        "Mastering the W.O.M.E.N. Framework for Influence",
        "Overcoming Gender-Based Barriers in Business",
        "Building Executive Presence & Personal Brand",
        "Strategic Networking for Women Leaders",
        "Negotiation & Advocacy Skills",
        "Mentorship, Sponsorship & Community Building",
        "Final Leadership Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la confiance et du leadership féminin",
        "Maîtriser le cadre W.O.M.E.N. pour l'influence",
        "Dépasser les barrières de genre en affaires",
        "Construire une présence exécutive et une marque personnelle",
        "Networking stratégique pour les femmes dirigeantes",
        "Compétences de négociation et de plaidoyer",
        "Mentorat, sponsoring et construction de communauté",
        "Stratégie finale de leadership et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Female Leadership: Influence, Confidence & Breakthrough for Women Entrepreneurs",
      fr: "Leadership féminin augmenté par l'IA : influence, confiance et percée pour les femmes entrepreneures",
      es: null, pt: null
    }
  },

  // ============================================================================
  // TRACK 4: FINANCE & FUNDING (6 bootcamps)
  // ============================================================================

  {
    id: "bc-04",
    slug: "financial-acumen-non-financial-managers",
    intro_video_url: "/__l5e/assets-v1/487e2acb-523f-4b90-9b7c-d177fbf5a232/fc70f08acd0f4d9d8ff1dcdeec9dbe46.mp4",
    track: "finance-funding",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: {
      en: "Financial Acumen for Non-Financial Managers",
      fr: "Acuité financière pour managers non-financiers",
      es: null, pt: null
    },
    card_tagline: {
      en: "Budgeting, forecasting, decisions",
      fr: "Budget, prévisions, décisions",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-driven financial decision-making, budgeting, and investment strategies for entrepreneurs and business leaders",
      fr: "Maîtriser la prise de décision financière pilotée par l'IA, le budget et les stratégies d'investissement pour entrepreneurs et dirigeants",
      es: null, pt: null
    },
    framework: "F.I.N.A.N.C.E.",
    framework_expansion: {
      en: "Fundamentals, Investments, Numbers, Analysis, Navigating risks, Cash Flow, Execution",
      fr: "Fondamentaux, Investissements, Chiffres, Analyse, Navigation des risques, Trésorerie, Exécution",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and business owners without finance backgrounds",
        "Non-financial managers making business decisions",
        "Startup founders needing financial literacy",
        "Professionals building financial decision-making skills"
      ],
      fr: [
        "Dirigeants sans formation financière",
        "Managers non-financiers prenant des décisions business",
        "Porteurs de projet ayant besoin de littératie financière",
        "Professionnels construisant leurs compétences de décision financière"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Financial Decision-Making",
        "Mastering the F.I.N.A.N.C.E. Framework",
        "Reading & Interpreting Financial Statements",
        "Budgeting, Forecasting & Cash Flow Management",
        "Investment Basics & Capital Allocation",
        "Risk Management & Financial Modeling",
        "AI-Powered Financial Analysis & Automation",
        "Final Financial Plan & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la décision financière",
        "Maîtriser le cadre F.I.N.A.N.C.E.",
        "Lire et interpréter les états financiers",
        "Budgétisation, prévisions et gestion de trésorerie",
        "Bases de l'investissement et allocation de capital",
        "Gestion des risques et modélisation financière",
        "Analyse financière et automatisation augmentées par l'IA",
        "Plan financier final et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: { en: null, fr: null, es: null, pt: null }
  },
  {
    id: "bc-27",
    slug: "profit-blueprint-young-entrepreneurs",
    intro_video_url: "/__l5e/assets-v1/a5e86304-8bdf-4ea4-8bae-5979b7976b21/1d617ba147cd47b8b1e3f53d8bb4736c.mp4",
    track: "finance-funding",
    level: "beginner",
    duration_weeks: 4,
    price_eur: 149,
    price_xof: 97800,
    is_new: true,
    title: {
      en: "The Profit Blueprint",
      fr: "Le plan profit",
      es: null, pt: null
    },
    card_tagline: {
      en: "Pricing, margins, reinvestment",
      fr: "Pricing, marges, réinvestissement",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered profit strategies, neuroscience-driven business growth, and high-impact revenue generation",
      fr: "Maîtriser les stratégies profit augmentées par l'IA, la croissance business appuyée par les neurosciences, et la génération de revenus à fort impact",
      es: null, pt: null
    },
    framework: "P.R.O.F.I.T.",
    framework_expansion: {
      en: "Pricing, Revenue Streams, Optimization, Financial Planning, Investments, Tracking",
      fr: "Pricing, Sources de revenus, Optimisation, Planification financière, Investissements, Suivi",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Young entrepreneurs in their first years of business",
        "Startup founders focused on profitability over growth-at-all-costs",
        "Small business owners increasing margins",
        "Freelancers and creators building profitable operations"
      ],
      fr: [
        "Jeunes entrepreneurs dans leurs premières années d'activité",
        "Porteurs de projet startup focus profitabilité plutôt que croissance à tout prix",
        "Dirigeants de PME augmentant leurs marges",
        "Freelances et créateurs construisant des opérations profitables"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Profitability & Money Mindset",
        "Mastering the P.R.O.F.I.T. Framework",
        "Pricing Strategy & Value Capture",
        "Revenue Stream Diversification",
        "Cost Management & Margin Optimization",
        "Financial Planning for Young Entrepreneurs",
        "Reinvestment & Compounding Growth",
        "Final Profit Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la profitabilité et de l'état d'esprit face à l'argent",
        "Maîtriser le cadre P.R.O.F.I.T.",
        "Stratégie de pricing et capture de valeur",
        "Diversification des sources de revenus",
        "Gestion des coûts et optimisation des marges",
        "Planification financière pour jeunes entrepreneurs",
        "Réinvestissement et croissance composée",
        "Stratégie profit finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Profit: The Blueprint for Young Entrepreneurs",
      fr: "Profit augmenté par l'IA : le plan pour jeunes entrepreneurs",
      es: null, pt: null
    }
  },
  {
    id: "bc-33",
    slug: "financing-business-growth",
    intro_video_url: "/__l5e/assets-v1/7457e792-0dfc-4c19-a424-ab9bc06bf0c0/d42b6490e32949e4ba3973a978f56a64.mp4",
    track: "finance-funding",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 229,
    price_xof: 150300,
    is_new: true,
    title: {
      en: "Financing Your Business Growth",
      fr: "Financer la croissance de son entreprise",
      es: null, pt: null
    },
    card_tagline: {
      en: "Debt, equity, grants, alternatives",
      fr: "Dette, equity, subventions, alternatives",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered funding strategies, neuroscience-driven investor psychology, and high-impact capital allocation",
      fr: "Maîtriser les stratégies de financement augmentées par l'IA, la psychologie investisseur appuyée par les neurosciences, et l'allocation de capital à fort impact",
      es: null, pt: null
    },
    framework: "F.U.N.D.",
    framework_expansion: {
      en: "Funding Options, Understanding Investors, Negotiation, Debt-Free Strategies",
      fr: "Options de financement, Comprendre les investisseurs, Négociation, Stratégies sans dette",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and business owners seeking growth capital",
        "Startup founders evaluating funding options",
        "Business leaders navigating debt vs. equity decisions",
        "Founders preparing for their first fundraise"
      ],
      fr: [
        "Porteurs de projet cherchant du capital de croissance",
        "Porteurs de projet startup évaluant leurs options de financement",
        "Dirigeants naviguant les décisions dette vs. equity",
        "Dirigeants préparant leur première levée de fonds"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Money & Investor Psychology",
        "Mastering the F.U.N.D. Framework",
        "Bootstrapping vs. Debt vs. Equity: Making the Right Call",
        "Preparing for Investor Meetings & Due Diligence",
        "Grants, Competitions & Non-Dilutive Funding",
        "Negotiating Term Sheets & Investor Relations",
        "Alternative Financing: Revenue-Based, Crowdfunding, Pre-Sales",
        "Final Funding Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de l'argent et de la psychologie investisseur",
        "Maîtriser le cadre F.U.N.D.",
        "Bootstrap vs. dette vs. equity : faire le bon choix",
        "Préparer les rendez-vous investisseurs et la due diligence",
        "Subventions, concours et financement non-dilutif",
        "Négocier term sheets et relations investisseurs",
        "Financement alternatif : revenue-based, crowdfunding, prévente",
        "Stratégie de financement finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Funding: The Future of Financing Business Growth",
      fr: "Financement augmenté par l'IA : le futur du financement de la croissance business",
      es: null, pt: null
    }
  },
  {
    id: "bc-40",
    slug: "investor-relations-fundraising-mastery",
    intro_video_url: "/__l5e/assets-v1/20ba8198-69eb-4f04-bee3-9957d2aa0fca/d8d599ec4eac4c3a803b518aa8f25345.mp4",
    track: "finance-funding",
    level: "advanced",
    duration_weeks: 6,
    price_eur: 299,
    price_xof: 196200,
    is_new: true,
    title: {
      en: "Investor Relations & Fundraising",
      fr: "Relations investisseurs et levée de fonds",
      es: null, pt: null
    },
    card_tagline: {
      en: "P.I.T.C.H. framework for founders",
      fr: "Cadre P.I.T.C.H. pour dirigeants",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered investor pitching, neuroscience-driven persuasion, and high-impact fundraising strategies",
      fr: "Maîtriser le pitch investisseur augmenté par l'IA, la persuasion appuyée par les neurosciences, et les stratégies de levée de fonds à fort impact",
      es: null, pt: null
    },
    framework: "P.I.T.C.H.",
    framework_expansion: {
      en: "Positioning, Investors, Traction, Communication, Handshake Closing",
      fr: "Positionnement, Investisseurs, Traction, Communication, Closing",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Startup founders actively fundraising or preparing to raise",
        "Entrepreneurs approaching seed, Series A, or growth rounds",
        "Business owners building investor relations programs",
        "Professionals supporting fundraising efforts"
      ],
      fr: [
        "Porteurs de projet startup en levée active ou en préparation",
        "Porteurs de projet approchant seed, Série A ou tours de croissance",
        "Dirigeants construisant des programmes de relations investisseurs",
        "Professionnels accompagnant les efforts de levée de fonds"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Investor Psychology & Decision-Making",
        "Mastering the P.I.T.C.H. Framework",
        "Building a Compelling Pitch Deck & Narrative",
        "Investor Targeting & Outreach Strategy",
        "Data Room Preparation & Due Diligence",
        "Negotiating Valuation, Terms & Deal Structure",
        "Post-Investment Communication & Reporting",
        "Final Fundraising Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la psychologie investisseur et de la prise de décision",
        "Maîtriser le cadre P.I.T.C.H.",
        "Construire un pitch deck et un récit convaincants",
        "Ciblage investisseurs et stratégie de prospection",
        "Préparation de data room et due diligence",
        "Négocier valorisation, termes et structure de deal",
        "Communication et reporting post-investissement",
        "Stratégie finale de levée de fonds et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Fundraising: The Future of Investor Relations",
      fr: "Levée de fonds augmentée par l'IA : le futur des relations investisseurs",
      es: null, pt: null
    }
  },
  {
    id: "bc-39",
    slug: "ai-personal-finance-wealth-building",
    intro_video_url: "/__l5e/assets-v1/39ffa6af-a96b-458b-bd6a-6416e6ccb9a0/f3e541642a08416a8f3356209b53176f.mp4",
    track: "finance-funding",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 179,
    price_xof: 117500,
    is_new: true,
    title: {
      en: "Personal Finance & Wealth Building",
      fr: "Finance personnelle et construction de richesse",
      es: null, pt: null
    },
    card_tagline: {
      en: "Long-term financial independence",
      fr: "Indépendance financière de long terme",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered wealth management, neuroscience-driven investment psychology, and high-impact financial independence",
      fr: "Maîtriser la gestion de patrimoine augmentée par l'IA, la psychologie d'investissement appuyée par les neurosciences, et l'indépendance financière à fort impact",
      es: null, pt: null
    },
    framework: "W.E.A.L.T.H.",
    framework_expansion: {
      en: "Wisdom, Earnings, Automation, Leverage, Time, Habits",
      fr: "Sagesse, Revenus, Automatisation, Effet de levier, Temps, Habitudes",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs building personal wealth alongside their business",
        "Founders separating business and personal finances",
        "Professionals building long-term financial independence",
        "High-earners optimizing their personal financial strategy"
      ],
      fr: [
        "Entrepreneurs construisant leur patrimoine personnel en parallèle de leur business",
        "Dirigeants séparant finances business et personnelles",
        "Professionnels construisant leur indépendance financière de long terme",
        "Hauts revenus optimisant leur stratégie financière personnelle"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Wealth Psychology & Money Mindset",
        "Mastering the W.E.A.L.T.H. Framework",
        "AI-Powered Budgeting, Saving & Expense Tracking",
        "Investing Fundamentals: Stocks, Real Estate, Alternative Assets",
        "Tax Optimization & Asset Protection",
        "Passive Income & Wealth Automation",
        "Long-Term Financial Planning & Retirement",
        "Final Wealth Strategy & AI Financial Validator"
      ],
      fr: [
        "Les neurosciences de la psychologie de la richesse et de l'état d'esprit face à l'argent",
        "Maîtriser le cadre W.E.A.L.T.H.",
        "Budget, épargne et suivi des dépenses augmentés par l'IA",
        "Fondamentaux de l'investissement : actions, immobilier, actifs alternatifs",
        "Optimisation fiscale et protection des actifs",
        "Revenus passifs et automatisation du patrimoine",
        "Planification financière de long terme et retraite",
        "Stratégie patrimoine finale et AI Financial Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Wealth: The Future of Personal Finance & Financial Independence",
      fr: "Patrimoine augmenté par l'IA : le futur de la finance personnelle et de l'indépendance financière",
      es: null, pt: null
    }
  },
  {
    id: "bc-31",
    slug: "macroeconomics-for-entrepreneurs",
    intro_video_url: "/__l5e/assets-v1/1569ed6f-2e01-4c8b-8cea-31df05d4e7d1/063d9b0a3efd454fb8f4e05c9df3bf7c.mp4",
    track: "finance-funding",
    level: "intermediate",
    duration_weeks: 4,
    price_eur: 179,
    price_xof: 117500,
    is_new: true,
    title: {
      en: "Macroeconomics for Entrepreneurs",
      fr: "Macroéconomie pour entrepreneurs",
      es: null, pt: null
    },
    card_tagline: {
      en: "Navigate inflation, rates, currency",
      fr: "Naviguer inflation, taux, devise",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered economic forecasting, neuroscience-driven market analysis, and high-impact strategic decision-making",
      fr: "Maîtriser la prévision économique augmentée par l'IA, l'analyse de marché appuyée par les neurosciences, et la décision stratégique à fort impact",
      es: null, pt: null
    },
    framework: "M.A.C.R.O.",
    framework_expansion: {
      en: "Market Trends, Adaptability, Capital, Risk, Optimization",
      fr: "Tendances de marché, Adaptabilité, Capital, Risque, Optimisation",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs navigating economic cycles",
        "Business owners making capital allocation decisions",
        "Founders operating across multiple markets",
        "Leaders planning through inflation, rates, and currency shifts"
      ],
      fr: [
        "Entrepreneurs naviguant les cycles économiques",
        "Dirigeants prenant des décisions d'allocation de capital",
        "Dirigeants opérant sur plusieurs marchés",
        "Dirigeants planifiant à travers inflation, taux et évolutions de devise"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Economic Decision-Making",
        "Mastering the M.A.C.R.O. Framework",
        "Understanding Inflation, Interest Rates & Currency",
        "Economic Cycles & Strategic Timing",
        "Global Trade, Supply Chains & Emerging Markets",
        "Government Policy & Regulatory Impact",
        "Capital Allocation Through Economic Shifts",
        "Final Macro Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la décision économique",
        "Maîtriser le cadre M.A.C.R.O.",
        "Comprendre inflation, taux d'intérêt et devise",
        "Cycles économiques et timing stratégique",
        "Commerce global, chaînes d'approvisionnement et marchés émergents",
        "Politique publique et impact réglementaire",
        "Allocation de capital à travers les basculements économiques",
        "Stratégie macro finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Macroeconomics: Strategic Decision-Making for Entrepreneurs",
      fr: "Macroéconomie augmentée par l'IA : décision stratégique pour entrepreneurs",
      es: null, pt: null
    }
  },

  // ============================================================================
  // TRACK 5: AI & DIGITAL MASTERY (7 bootcamps)
  // ============================================================================
  {
    id: "bc-22",
    slug: "ai-mastery-cutting-costs-productivity",
    intro_video_url: "/__l5e/assets-v1/4585f557-da07-480a-8ea7-de2047a8a1ab/af455f1e37ff4231bf4456947bc06fbe.mp4",
    track: "ai-digital",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: { en: "AI Mastery", fr: "Maîtrise de l'IA", es: null, pt: null },
    card_tagline: {
      en: "Cut costs, boost productivity",
      fr: "Réduire les coûts, dynamiser la productivité",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered automation, neuroscience-driven productivity, and high-impact cost reduction strategies",
      fr: "Maîtriser l'automatisation augmentée par l'IA, la productivité appuyée par les neurosciences, et les stratégies de réduction des coûts à fort impact",
      es: null, pt: null
    },
    framework: "A.U.T.O.M.A.T.E.",
    framework_expansion: {
      en: "AI, Utilization, Task Optimization, Operations, Metrics, Adaptation, Time Management, Efficiency",
      fr: "IA, Utilisation, Optimisation des tâches, Opérations, Indicateurs, Adaptation, Gestion du temps, Efficience",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs cutting operational costs with AI",
        "Small business owners automating workflows",
        "Startup founders scaling without proportional headcount",
        "Professionals integrating AI into daily operations"
      ],
      fr: [
        "Entrepreneurs réduisant les coûts opérationnels avec l'IA",
        "Dirigeants de PME automatisant leurs flux de travail",
        "Porteurs de projet startup scalant sans recrutement proportionnel",
        "Professionnels intégrant l'IA dans leurs opérations quotidiennes"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of AI Adoption & Productivity",
        "Mastering the A.U.T.O.M.A.T.E. Framework",
        "AI Tools for Marketing, Sales & Customer Service",
        "AI-Driven Operations & Back-Office Automation",
        "AI in Finance, Bookkeeping & Analytics",
        "Building AI Workflows Without Code",
        "Measuring AI ROI & Continuous Optimization",
        "Final AI Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de l'adoption de l'IA et de la productivité",
        "Maîtriser le cadre A.U.T.O.M.A.T.E.",
        "Outils IA pour marketing, ventes et service client",
        "Opérations pilotées par l'IA et automatisation du back-office",
        "IA en finance, comptabilité et analytics",
        "Construire des workflows IA sans code",
        "Mesurer le ROI de l'IA et optimisation continue",
        "Stratégie IA finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Business: Cutting Costs & Supercharging Productivity",
      fr: "Business augmenté par l'IA : réduire les coûts et dynamiser la productivité",
      es: null, pt: null
    }
  },
  {
    id: "bc-32",
    slug: "succeeding-with-ai-entrepreneurs",
    intro_video_url: "/__l5e/assets-v1/3624cee3-f6fb-4d35-8dee-19270f8c576d/d07b18ad7cfb44ca96692499ebcb6499.mp4",
    track: "ai-digital",
    level: "beginner",
    duration_weeks: 4,
    price_eur: 149,
    price_xof: 97800,
    is_new: true,
    title: { en: "Succeeding with AI", fr: "Réussir avec l'IA", es: null, pt: null },
    card_tagline: {
      en: "AI fundamentals for founders",
      fr: "Fondamentaux de l'IA pour dirigeants",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI fundamentals, neuroscience-driven tool selection, and high-impact AI integration for business",
      fr: "Maîtriser les fondamentaux de l'IA, la sélection d'outils appuyée par les neurosciences, et l'intégration IA à fort impact pour les affaires",
      es: null, pt: null
    },
    framework: "A.D.A.P.T.",
    framework_expansion: {
      en: "Automate, Data-Driven, AI Tools, Productivity, Transformation",
      fr: "Automatiser, Data-driven, Outils IA, Productivité, Transformation",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs starting with AI for the first time",
        "Business owners evaluating AI tools",
        "Non-technical founders integrating AI",
        "Professionals building AI literacy"
      ],
      fr: [
        "Entrepreneurs démarrant avec l'IA pour la première fois",
        "Dirigeants évaluant les outils IA",
        "Porteurs de projet non-techniques intégrant l'IA",
        "Professionnels construisant leur littératie IA"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of AI & Decision-Making",
        "Mastering the A.D.A.P.T. Framework",
        "AI Tool Selection: What Works for Your Business",
        "Prompt Engineering & AI Communication Skills",
        "AI for Content, Marketing & Sales",
        "AI for Operations & Customer Experience",
        "Ethics, Bias & Responsible AI Use",
        "Final AI Integration Plan & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de l'IA et de la prise de décision",
        "Maîtriser le cadre A.D.A.P.T.",
        "Sélection d'outils IA : ce qui fonctionne pour votre business",
        "Prompt engineering et compétences de communication avec l'IA",
        "IA pour le contenu, le marketing et les ventes",
        "IA pour les opérations et l'expérience client",
        "Éthique, biais et usage responsable de l'IA",
        "Plan d'intégration IA final et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "Succeeding with AI: A Practical Guide for Entrepreneurs",
      fr: "Réussir avec l'IA : guide pratique pour entrepreneurs",
      es: null, pt: null
    }
  },
  {
    id: "bc-28",
    slug: "knowledge-enterprise-chatgpt",
    intro_video_url: "/__l5e/assets-v1/cfc28abb-90fa-45f1-ab39-577a9f31e469/33bf189a487749b29a6729fabff88301.mp4",
    track: "ai-digital",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: {
      en: "Knowledge to Enterprise with ChatGPT",
      fr: "Du savoir à l'entreprise avec ChatGPT",
      es: null, pt: null
    },
    card_tagline: {
      en: "Monetize expertise with AI",
      fr: "Monétiser son expertise avec l'IA",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered business building, neuroscience-driven content strategy, and high-impact monetization with ChatGPT",
      fr: "Maîtriser la construction business augmentée par ChatGPT, la stratégie de contenu appuyée par les neurosciences, et la monétisation à fort impact",
      es: null, pt: null
    },
    framework: "C.H.A.T.G.P.T.",
    framework_expansion: {
      en: "Create, Harness, Automate, Target, Generate, Profit, Transform",
      fr: "Créer, Exploiter, Automatiser, Cibler, Générer, Profit, Transformer",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs turning expertise into a business",
        "Consultants and coaches productizing their knowledge",
        "Creators and educators building digital offerings",
        "Solo founders leveraging AI to scale without a team"
      ],
      fr: [
        "Entrepreneurs transformant leur expertise en business",
        "Consultants et coachs productisant leur savoir",
        "Créateurs et éducateurs construisant des offres digitales",
        "Porteurs de projet solo exploitant l'IA pour scaler sans équipe"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Knowledge Monetization",
        "Mastering the C.H.A.T.G.P.T. Framework",
        "Turning Expertise into Digital Products",
        "Content Creation at Scale with ChatGPT",
        "AI-Powered Marketing & Customer Acquisition",
        "Automating Customer Service & Operations",
        "Monetization: Courses, Coaching, SaaS, Consulting",
        "Final Business Strategy & AI Validator"
      ],
      fr: [
        "Les neurosciences de la monétisation du savoir",
        "Maîtriser le cadre C.H.A.T.G.P.T.",
        "Transformer l'expertise en produits digitaux",
        "Création de contenu à grande échelle avec ChatGPT",
        "Marketing et acquisition client augmentés par l'IA",
        "Automatiser le service client et les opérations",
        "Monétisation : formations, coaching, SaaS, conseil",
        "Stratégie business finale et AI Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Entrepreneurship: Building Your Business with ChatGPT",
      fr: "Entrepreneuriat augmenté par l'IA : construire son business avec ChatGPT",
      es: null, pt: null
    }
  },
  {
    id: "bc-08",
    slug: "digital-transformation-innovation-mastery",
    intro_video_url: "/__l5e/assets-v1/684ab90b-6e9b-4adb-8cad-64a13cf894e8/349dbe6b8df9439c8e727297557aacee.mp4",
    track: "ai-digital",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 229,
    price_xof: 150300,
    is_new: true,
    title: {
      en: "Digital Transformation Mastery",
      fr: "Maîtrise de la transformation digitale",
      es: null, pt: null
    },
    card_tagline: {
      en: "Modernize legacy operations",
      fr: "Moderniser les opérations legacy",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-driven transformation, neuroscience-backed change strategies, and high-impact digital innovation",
      fr: "Maîtriser la transformation pilotée par l'IA, les stratégies de changement appuyées par les neurosciences, et l'innovation digitale à fort impact",
      es: null, pt: null
    },
    framework: "D.I.G.I.T.A.L.",
    framework_expansion: {
      en: "Disrupt, Innovate, Grow, Integrate, Transform, Automate, Lead",
      fr: "Disrupter, Innover, Grandir, Intégrer, Transformer, Automatiser, Diriger",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Business leaders driving digital transformation",
        "Entrepreneurs modernizing legacy operations",
        "Startup founders building digital-first businesses",
        "Executives orchestrating technology adoption"
      ],
      fr: [
        "Dirigeants pilotant la transformation digitale",
        "Porteurs de projet modernisant des opérations legacy",
        "Porteurs de projet startup construisant des entreprises digital-first",
        "Cadres orchestrant l'adoption technologique"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Digital Adoption & Change",
        "Mastering the D.I.G.I.T.A.L. Framework",
        "Digital Strategy & Roadmapping",
        "Cloud, Data & Infrastructure Foundations",
        "Automation, AI & Emerging Tech Integration",
        "Digital Customer Experience & Engagement",
        "Change Management for Digital Transformation",
        "Final Digital Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de l'adoption digitale et du changement",
        "Maîtriser le cadre D.I.G.I.T.A.L.",
        "Stratégie digitale et roadmapping",
        "Cloud, data et fondations d'infrastructure",
        "Automatisation, IA et intégration des tech émergentes",
        "Expérience client digitale et engagement",
        "Change management pour la transformation digitale",
        "Stratégie digitale finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Digital Transformation: The Future of Business Innovation",
      fr: "Transformation digitale augmentée par l'IA : le futur de l'innovation business",
      es: null, pt: null
    }
  },
  {
    id: "bc-38",
    slug: "digital-asset-creation-monetization",
    intro_video_url: "/__l5e/assets-v1/98e2ae52-9420-4fa1-99cc-a8c8866a59b3/7f19ef07630d42fd86d8a4a9e9877939.mp4",
    track: "ai-digital",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: {
      en: "Digital Asset Creation",
      fr: "Création d'actifs numériques",
      es: null, pt: null
    },
    card_tagline: {
      en: "Build and monetize online products",
      fr: "Construire et monétiser des produits en ligne",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered digital product creation, neuroscience-driven engagement, and high-impact monetization strategies",
      fr: "Maîtriser la création de produits numériques augmentée par l'IA, l'engagement appuyé par les neurosciences, et les stratégies de monétisation à fort impact",
      es: null, pt: null
    },
    framework: "M.O.N.E.T.I.Z.E.",
    framework_expansion: {
      en: "Market, Optimize, Nurture, Earn, Traffic, Iterate, Zone of Genius, Expand",
      fr: "Marché, Optimiser, Nurture, Gagner, Trafic, Itérer, Zone de génie, Étendre",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Creators and entrepreneurs building digital products",
        "Consultants packaging expertise into assets",
        "Content creators monetizing audiences",
        "Solo founders building digital revenue streams"
      ],
      fr: [
        "Créateurs et entrepreneurs construisant des produits numériques",
        "Consultants transformant leur expertise en actifs",
        "Créateurs de contenu monétisant leur audience",
        "Porteurs de projet solo construisant des sources de revenus digitales"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Digital Consumer Behavior",
        "Mastering the M.O.N.E.T.I.Z.E. Framework",
        "Digital Product Ideation & Validation",
        "Courses, eBooks, Templates & Digital Downloads",
        "SaaS, Memberships & Recurring Revenue Models",
        "AI-Powered Digital Marketing & Sales Funnels",
        "Scaling Digital Businesses & Automation",
        "Final Digital Asset Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences du comportement consommateur digital",
        "Maîtriser le cadre M.O.N.E.T.I.Z.E.",
        "Idéation et validation de produits numériques",
        "Formations, eBooks, templates et téléchargements digitaux",
        "SaaS, adhésions et modèles de revenus récurrents",
        "Marketing digital et tunnels de vente augmentés par l'IA",
        "Scaler des business digitaux et automatisation",
        "Stratégie finale d'actifs digitaux et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Digital Assets: The Future of Online Monetization",
      fr: "Actifs numériques augmentés par l'IA : le futur de la monétisation en ligne",
      es: null, pt: null
    }
  },
  {
    id: "bc-43",
    slug: "business-intelligence-data-driven-decisions",
    intro_video_url: "/__l5e/assets-v1/2f8cd6cb-3eaf-4c89-98fc-dfce6df64786/c18cc1264469474ba4966b70902f35c7.mp4",
    track: "ai-digital",
    level: "advanced",
    duration_weeks: 5,
    price_eur: 249,
    price_xof: 163400,
    is_new: true,
    title: {
      en: "Business Intelligence & Data",
      fr: "Business intelligence et data",
      es: null, pt: null
    },
    card_tagline: {
      en: "KPIs, dashboards, analytics",
      fr: "KPIs, tableaux de bord, analytics",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered analytics, neuroscience-driven data interpretation, and high-impact business intelligence",
      fr: "Maîtriser l'analytics augmenté par l'IA, l'interprétation des données appuyée par les neurosciences, et la business intelligence à fort impact",
      es: null, pt: null
    },
    framework: "D.A.T.A.",
    framework_expansion: {
      en: "Data, Analysis, Tracking, Action",
      fr: "Données, Analyse, Suivi, Action",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Business leaders making data-informed decisions",
        "Entrepreneurs building analytics-driven operations",
        "Managers interpreting KPIs and dashboards",
        "Professionals building BI literacy"
      ],
      fr: [
        "Dirigeants prenant des décisions informées par les données",
        "Entrepreneurs construisant des opérations pilotées par l'analytics",
        "Managers interprétant KPIs et tableaux de bord",
        "Professionnels construisant leur littératie BI"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Data-Driven Thinking",
        "Mastering the D.A.T.A. Framework",
        "Defining the Right KPIs for Your Business",
        "Dashboards, Tracking & Reporting Systems",
        "AI-Powered Analytics & Predictive Models",
        "Data Storytelling & Communication",
        "From Insight to Action: Closing the Loop",
        "Final BI Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la pensée data-driven",
        "Maîtriser le cadre D.A.T.A.",
        "Définir les bons KPIs pour son business",
        "Tableaux de bord, suivi et systèmes de reporting",
        "Analytics et modèles prédictifs augmentés par l'IA",
        "Data storytelling et communication",
        "De l'insight à l'action : boucler la boucle",
        "Stratégie BI finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Business Intelligence: The Future of Data-Driven Decisions",
      fr: "Business intelligence augmentée par l'IA : le futur des décisions data-driven",
      es: null, pt: null
    }
  },
  {
    id: "bc-37",
    slug: "future-work-remote-hybrid-teams",
    intro_video_url: "/__l5e/assets-v1/3ee2bd2f-416c-44d6-ba41-b061451dcae5/3bc90c04de4443c3a7376025700241f7.mp4",
    track: "ai-digital",
    level: "intermediate",
    duration_weeks: 4,
    price_eur: 179,
    price_xof: 117500,
    is_new: true,
    title: {
      en: "Remote & Hybrid Team Leadership",
      fr: "Leadership d'équipes remote et hybrides",
      es: null, pt: null
    },
    card_tagline: {
      en: "Lead distributed teams well",
      fr: "Diriger efficacement les équipes distribuées",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered remote leadership, neuroscience-driven team engagement, and high-impact distributed work strategies",
      fr: "Maîtriser le leadership remote augmenté par l'IA, l'engagement d'équipe appuyé par les neurosciences, et les stratégies de travail distribué à fort impact",
      es: null, pt: null
    },
    framework: "L.E.A.D.",
    framework_expansion: {
      en: "Listen, Empower, Adapt, Deliver",
      fr: "Écouter, Responsabiliser, Adapter, Livrer",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Business leaders managing remote or hybrid teams",
        "Entrepreneurs building distributed-first organizations",
        "HR professionals designing remote work policies",
        "Managers navigating the future of work"
      ],
      fr: [
        "Dirigeants gérant des équipes remote ou hybrides",
        "Porteurs de projet construisant des organisations distributed-first",
        "Professionnels RH concevant des politiques de travail à distance",
        "Managers naviguant le futur du travail"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Remote Work & Distributed Teams",
        "Mastering Remote Leadership Fundamentals",
        "Building Remote Team Culture & Trust",
        "Async Communication & Documentation Systems",
        "Tools & Technology for Distributed Work",
        "Hybrid Work Models & Office Design",
        "Performance Management in Remote Teams",
        "Final Remote Leadership Strategy & AI Validator"
      ],
      fr: [
        "Les neurosciences du travail remote et des équipes distribuées",
        "Maîtriser les fondamentaux du leadership remote",
        "Construire culture et confiance en équipe remote",
        "Communication asynchrone et systèmes de documentation",
        "Outils et technologie pour le travail distribué",
        "Modèles de travail hybride et design des bureaux",
        "Gestion de la performance en équipe remote",
        "Stratégie finale de leadership remote et AI Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Remote Leadership: The Future of Distributed Work",
      fr: "Leadership remote augmenté par l'IA : le futur du travail distribué",
      es: null, pt: null
    }
  },

  // ============================================================================
  // TRACK 6: MARKETING & SALES (6 bootcamps)
  // ============================================================================
  {
    id: "bc-05",
    slug: "marketing-strategies-business-growth",
    intro_video_url: "/__l5e/assets-v1/42e1379f-edce-4bda-96ed-35142d37f8b3/b308b7473e714ba09a5852689b684364.mp4",
    track: "marketing-sales",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: {
      en: "Marketing Strategies for Growth",
      fr: "Stratégies marketing pour la croissance",
      es: null, pt: null
    },
    card_tagline: {
      en: "M.A.R.K.E.T. framework mastery",
      fr: "Maîtrise du cadre M.A.R.K.E.T.",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered marketing, neuroscience-driven consumer behavior, and high-impact growth campaigns",
      fr: "Maîtriser le marketing augmenté par l'IA, le comportement consommateur appuyé par les neurosciences, et les campagnes de croissance à fort impact",
      es: null, pt: null
    },
    framework: "M.A.R.K.E.T.",
    framework_expansion: {
      en: "Messaging, Audience, Reach, KPIs, Engagement, Tactics",
      fr: "Messaging, Audience, Couverture, KPIs, Engagement, Tactiques",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and business owners building marketing engines",
        "Startup founders driving customer acquisition",
        "Marketing professionals upgrading their strategy",
        "Non-marketing leaders needing marketing fluency"
      ],
      fr: [
        "Porteurs de projet construisant leurs moteurs marketing",
        "Porteurs de projet startup pilotant l'acquisition client",
        "Professionnels du marketing faisant évoluer leur stratégie",
        "Dirigeants non-marketing ayant besoin de fluidité marketing"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Consumer Behavior & Marketing",
        "Mastering the M.A.R.K.E.T. Framework",
        "Brand Positioning & Value Proposition",
        "Digital Marketing Channels: SEO, Paid, Social, Email",
        "AI-Powered Marketing & Automation",
        "Data-Driven Campaign Optimization",
        "Customer Retention & Lifetime Value",
        "Final Marketing Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences du comportement consommateur et du marketing",
        "Maîtriser le cadre M.A.R.K.E.T.",
        "Positionnement de marque et proposition de valeur",
        "Canaux marketing digital : SEO, paid, social, email",
        "Marketing augmenté par l'IA et automatisation",
        "Optimisation de campagnes data-driven",
        "Rétention client et valeur vie client",
        "Stratégie marketing finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: { en: null, fr: null, es: null, pt: null }
  },
  {
    id: "bc-07",
    slug: "advanced-sales-techniques-crm",
    intro_video_url: "/__l5e/assets-v1/f0b2f274-7303-4b92-a467-9e762278442f/3134ee5878c0416f81c3a5fce9c3c8cb.mp4",
    track: "marketing-sales",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: {
      en: "Advanced Sales Techniques & CRM",
      fr: "Techniques de vente avancées et CRM",
      es: null, pt: null
    },
    card_tagline: {
      en: "Pipeline, closing, expansion",
      fr: "Pipeline, closing, expansion",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered selling, neuroscience-driven persuasion, and high-impact sales systems",
      fr: "Maîtriser la vente augmentée par l'IA, la persuasion appuyée par les neurosciences, et les systèmes de vente à fort impact",
      es: null, pt: null
    },
    framework: "S.E.L.L.",
    framework_expansion: {
      en: "Strategy, Engagement, Listening, Leverage",
      fr: "Stratégie, Engagement, Écoute, Effet de levier",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and business owners closing deals",
        "Sales professionals and account executives",
        "Startup founders selling their first contracts",
        "Business development leaders scaling sales teams"
      ],
      fr: [
        "Porteurs de projet et dirigeants closant des deals",
        "Professionnels de la vente et account executives",
        "Porteurs de projet startup vendant leurs premiers contrats",
        "Responsables business development scalant leurs équipes de vente"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Sales & Buyer Psychology",
        "Mastering the S.E.L.L. Framework",
        "Prospecting, Qualification & Discovery",
        "Handling Objections & Closing Deals",
        "CRM Systems & Pipeline Management",
        "AI-Powered Sales Automation & Intelligence",
        "Negotiation & Account Expansion",
        "Final Sales Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la vente et de la psychologie acheteur",
        "Maîtriser le cadre S.E.L.L.",
        "Prospection, qualification et découverte",
        "Gestion des objections et closing",
        "Systèmes CRM et gestion de pipeline",
        "Automatisation et intelligence commerciales augmentées par l'IA",
        "Négociation et expansion de comptes",
        "Stratégie de vente finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: { en: null, fr: null, es: null, pt: null }
  },
  {
    id: "bc-41",
    slug: "digital-product-growth-hacking",
    intro_video_url: "/__l5e/assets-v1/a3dfadac-018d-4c36-88b8-d7f03f2e9f29/7455cbc4dbc04a5fb998c071bd0ea3b3.mp4",
    track: "marketing-sales",
    level: "advanced",
    duration_weeks: 5,
    price_eur: 229,
    price_xof: 150300,
    is_new: true,
    title: {
      en: "Digital Product Growth Hacking",
      fr: "Growth hacking pour produits numériques",
      es: null, pt: null
    },
    card_tagline: {
      en: "Viral loops and hyper-scale",
      fr: "Boucles virales et hyper-scale",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered growth experiments, neuroscience-driven virality, and high-impact user acquisition",
      fr: "Maîtriser les expériences de croissance augmentées par l'IA, la viralité appuyée par les neurosciences, et l'acquisition d'utilisateurs à fort impact",
      es: null, pt: null
    },
    framework: "G.R.O.W.T.H.",
    framework_expansion: {
      en: "Generate, Reach, Optimize, Win, Track, Hyper-Scale",
      fr: "Générer, Atteindre, Optimiser, Gagner, Suivre, Hyper-scaler",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Founders of digital products and SaaS",
        "Growth marketers and product managers",
        "Startup founders running growth experiments",
        "Entrepreneurs scaling digital user bases"
      ],
      fr: [
        "Porteurs de projet de produits numériques et SaaS",
        "Growth marketers et chefs de produit",
        "Porteurs de projet startup menant des expériences de croissance",
        "Entrepreneurs scalant des bases d'utilisateurs digitales"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Viral Growth & User Behavior",
        "Mastering the G.R.O.W.T.H. Framework",
        "Acquisition Channels & User Funnels",
        "Activation, Retention & Engagement Loops",
        "Referral & Virality Mechanics",
        "AI-Powered Growth Experiments & Analytics",
        "Scaling Growth Systems & Hyper-Scale Strategies",
        "Final Growth Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la croissance virale et du comportement utilisateur",
        "Maîtriser le cadre G.R.O.W.T.H.",
        "Canaux d'acquisition et tunnels utilisateur",
        "Activation, rétention et boucles d'engagement",
        "Mécaniques de parrainage et de viralité",
        "Expériences de croissance et analytics augmentés par l'IA",
        "Scaler les systèmes de croissance et stratégies hyper-scale",
        "Stratégie de croissance finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Growth Hacking: The Future of Digital Product Scaling",
      fr: "Growth hacking augmenté par l'IA : le futur du scale des produits numériques",
      es: null, pt: null
    }
  },
  {
    id: "bc-42",
    slug: "neuromarketing-consumer-psychology",
    intro_video_url: "/__l5e/assets-v1/bd1ec74c-2230-4b09-b585-236219edf0cc/b46eed08d46542898da1a04a96c05b20.mp4",
    track: "marketing-sales",
    level: "advanced",
    duration_weeks: 5,
    price_eur: 229,
    price_xof: 150300,
    is_new: true,
    title: { en: "Neuro-Marketing", fr: "Neuro-marketing", es: null, pt: null },
    card_tagline: {
      en: "Science of persuasion",
      fr: "Science de la persuasion",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered consumer insights, neuroscience-driven persuasion, and high-impact brand psychology",
      fr: "Maîtriser les insights consommateur augmentés par l'IA, la persuasion appuyée par les neurosciences, et la psychologie de marque à fort impact",
      es: null, pt: null
    },
    framework: "P.E.R.S.U.A.D.E.",
    framework_expansion: {
      en: "Psychology, Emotion, Resonance, Storytelling, Urgency, Authority, Decision-Making, Engagement",
      fr: "Psychologie, Émotion, Résonance, Storytelling, Urgence, Autorité, Prise de décision, Engagement",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Marketers and brand strategists",
        "Entrepreneurs building emotionally resonant brands",
        "Creative directors and content leads",
        "Business leaders mastering persuasion"
      ],
      fr: [
        "Marketers et stratèges de marque",
        "Entrepreneurs construisant des marques émotionnellement résonantes",
        "Directeurs créatifs et responsables contenu",
        "Dirigeants maîtrisant la persuasion"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Consumer Decision-Making",
        "Mastering the P.E.R.S.U.A.D.E. Framework",
        "Emotional Triggers & Brand Storytelling",
        "Pricing Psychology & Value Perception",
        "Colors, Design & Sensory Branding",
        "Social Proof, Authority & Influence",
        "AI-Powered Consumer Research",
        "Final Neuro-Marketing Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la décision consommateur",
        "Maîtriser le cadre P.E.R.S.U.A.D.E.",
        "Déclencheurs émotionnels et storytelling de marque",
        "Psychologie du pricing et perception de valeur",
        "Couleurs, design et branding sensoriel",
        "Preuve sociale, autorité et influence",
        "Recherche consommateur augmentée par l'IA",
        "Stratégie neuro-marketing finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Neuro-Marketing: The Science of Persuasion",
      fr: "Neuro-marketing augmenté par l'IA : la science de la persuasion",
      es: null, pt: null
    }
  },
  {
    id: "bc-14",
    slug: "personal-branding-professional-networking",
    intro_video_url: "/__l5e/assets-v1/a0653798-6b32-49c3-914a-0a51aee57d2d/4a399c5a070844cdbfcb4a0d84e85407.mp4",
    track: "marketing-sales",
    level: "intermediate",
    duration_weeks: 4,
    price_eur: 149,
    price_xof: 97800,
    is_new: true,
    title: {
      en: "Personal Branding & Networking",
      fr: "Marque personnelle et networking",
      es: null, pt: null
    },
    card_tagline: {
      en: "Build authority on LinkedIn",
      fr: "Construire son autorité sur LinkedIn",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered personal branding, neuroscience-driven influence, and high-impact networking strategies",
      fr: "Maîtriser la marque personnelle augmentée par l'IA, l'influence appuyée par les neurosciences, et les stratégies de networking à fort impact",
      es: null, pt: null
    },
    framework: "B.R.A.N.D.",
    framework_expansion: {
      en: "Build, Reinforce, Amplify, Network, Dominate",
      fr: "Construire, Renforcer, Amplifier, Réseauter, Dominer",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs building personal brands",
        "Founders becoming industry thought leaders",
        "Professionals growing LinkedIn presence",
        "Consultants and creators building audiences"
      ],
      fr: [
        "Entrepreneurs construisant leur marque personnelle",
        "Porteurs de projet devenant leaders d'opinion dans leur industrie",
        "Professionnels développant leur présence LinkedIn",
        "Consultants et créateurs construisant leurs audiences"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Personal Branding",
        "Mastering the B.R.A.N.D. Framework",
        "Positioning & Differentiation Strategy",
        "Content Strategy for Thought Leadership",
        "LinkedIn, Twitter & Social Platform Mastery",
        "AI-Powered Content Creation & Distribution",
        "Strategic Networking & Relationship Building",
        "Final Personal Brand Strategy & AI Validator"
      ],
      fr: [
        "Les neurosciences de la marque personnelle",
        "Maîtriser le cadre B.R.A.N.D.",
        "Stratégie de positionnement et différenciation",
        "Stratégie de contenu pour le leadership d'opinion",
        "Maîtrise LinkedIn, Twitter et plateformes sociales",
        "Création et distribution de contenu augmentées par l'IA",
        "Networking stratégique et construction de relations",
        "Stratégie finale de marque personnelle et AI Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Personal Branding: The Future of Professional Influence",
      fr: "Marque personnelle augmentée par l'IA : le futur de l'influence professionnelle",
      es: null, pt: null
    }
  },
  {
    id: "bc-25",
    slug: "executive-communication-ceo",
    intro_video_url: "/__l5e/assets-v1/85699ab6-81d5-4acd-9978-1043d3697f91/621b38dddd8e42f7bfb8ac07fe3f0c84.mp4",
    track: "marketing-sales",
    level: "intermediate",
    duration_weeks: 4,
    price_eur: 179,
    price_xof: 117500,
    is_new: true,
    title: {
      en: "Executive Communication",
      fr: "Communication exécutive",
      es: null, pt: null
    },
    card_tagline: {
      en: "Speak like a startup CEO",
      fr: "Parler comme un DG de startup",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered executive presence, neuroscience-driven public speaking, and high-impact leadership communication",
      fr: "Maîtriser la présence exécutive augmentée par l'IA, la prise de parole appuyée par les neurosciences, et la communication leadership à fort impact",
      es: null, pt: null
    },
    framework: "S.P.E.A.K.",
    framework_expansion: {
      en: "Structure, Presence, Emotion, Authenticity, Knowledge",
      fr: "Structure, Présence, Émotion, Authenticité, Connaissance",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "CEOs and founders communicating with stakeholders",
        "Executives presenting to boards and investors",
        "Leaders delivering keynotes and public speeches",
        "Startup founders polishing their executive presence"
      ],
      fr: [
        "DG et dirigeants communiquant avec leurs parties prenantes",
        "Cadres présentant aux conseils et investisseurs",
        "Dirigeants livrant des keynotes et discours publics",
        "Porteurs de projet startup peaufinant leur présence exécutive"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Executive Presence",
        "Mastering the S.P.E.A.K. Framework",
        "Storytelling for Leaders",
        "Pitching Investors & Boards",
        "Handling Media, Press & Public Q&A",
        "AI-Powered Speech Preparation & Delivery",
        "Crisis Communication & Difficult Messages",
        "Final Communication Strategy & AI Validator"
      ],
      fr: [
        "Les neurosciences de la présence exécutive",
        "Maîtriser le cadre S.P.E.A.K.",
        "Storytelling pour dirigeants",
        "Pitcher investisseurs et conseils",
        "Gérer médias, presse et Q&R publiques",
        "Préparation et livraison de discours augmentées par l'IA",
        "Communication de crise et messages difficiles",
        "Stratégie de communication finale et AI Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Executive Communication: The Future of Leadership Speech",
      fr: "Communication exécutive augmentée par l'IA : le futur du discours de leadership",
      es: null, pt: null
    }
  },

  // ============================================================================
  // TRACK 7: OPERATIONS, RISK & GOVERNANCE (6 bootcamps)
  // ============================================================================
  {
    id: "bc-13",
    slug: "risk-management-regulatory-compliance",
    intro_video_url: "/__l5e/assets-v1/2449c606-dab3-4c2a-a49a-abe2082126bb/48a15730482f43e692980e4e8c66cdaf.mp4",
    track: "operations-risk",
    level: "advanced",
    duration_weeks: 5,
    price_eur: 249,
    price_xof: 163400,
    is_new: true,
    title: {
      en: "Risk Management & Compliance",
      fr: "Gestion des risques et conformité",
      es: null, pt: null
    },
    card_tagline: {
      en: "Enterprise risk for founders",
      fr: "Risque d'entreprise pour dirigeants",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered risk assessment, neuroscience-driven decision-making, and high-impact compliance strategies",
      fr: "Maîtriser l'évaluation des risques augmentée par l'IA, la décision appuyée par les neurosciences, et les stratégies de conformité à fort impact",
      es: null, pt: null
    },
    framework: "R.I.S.K.",
    framework_expansion: {
      en: "Recognize, Identify, Secure, Keep Adjusting",
      fr: "Reconnaître, Identifier, Sécuriser, Ajuster en continu",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Business leaders managing enterprise risk",
        "Entrepreneurs in regulated industries",
        "Compliance officers and risk managers",
        "Founders navigating UEMOA and OHADA regulatory frameworks"
      ],
      fr: [
        "Dirigeants gérant le risque d'entreprise",
        "Entrepreneurs dans des industries réglementées",
        "Responsables conformité et gestionnaires de risques",
        "Dirigeants naviguant les cadres réglementaires UEMOA et OHADA"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Risk Perception & Decision-Making",
        "Mastering the R.I.S.K. Framework",
        "Enterprise Risk Assessment & Mapping",
        "Regulatory Compliance Essentials",
        "Financial, Operational & Cyber Risk",
        "Crisis Management & Business Continuity",
        "AI-Powered Risk Monitoring & Prediction",
        "Final Risk Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la perception du risque et de la décision",
        "Maîtriser le cadre R.I.S.K.",
        "Évaluation et cartographie du risque d'entreprise",
        "Essentiels de la conformité réglementaire",
        "Risque financier, opérationnel et cyber",
        "Gestion de crise et continuité d'activité",
        "Monitoring et prédiction du risque augmentés par l'IA",
        "Stratégie de risque finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Risk Management: The Future of Compliance & Business Resilience",
      fr: "Gestion des risques augmentée par l'IA : le futur de la conformité et de la résilience business",
      es: null, pt: null
    }
  },
  {
    id: "bc-34",
    slug: "hse-management-startups-small-business",
    intro_video_url: "/__l5e/assets-v1/60cdfd4c-448b-467a-acc3-4c02e333dfc4/b701ea6e663d4247acad4be46b701392.mp4",
    track: "operations-risk",
    level: "intermediate",
    duration_weeks: 4,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: {
      en: "HSE Management for SMEs",
      fr: "Management HSE pour PME",
      es: null, pt: null
    },
    card_tagline: {
      en: "Safety culture and compliance",
      fr: "Culture sécurité et conformité",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered safety management, neuroscience-driven compliance culture, and high-impact HSE strategies",
      fr: "Maîtriser le management de la sécurité augmenté par l'IA, la culture de conformité appuyée par les neurosciences, et les stratégies HSE à fort impact",
      es: null, pt: null
    },
    framework: "S.A.F.E.T.Y.",
    framework_expansion: {
      en: "Standards, Awareness, Forecasting, Execution, Training, Yielding Results",
      fr: "Normes, Conscience, Prévision, Exécution, Formation, Résultats",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs building HSE-compliant operations",
        "Small business owners in manufacturing, construction, energy",
        "HSE managers in emerging-market SMEs",
        "Founders navigating health and safety regulations"
      ],
      fr: [
        "Entrepreneurs construisant des opérations conformes HSE",
        "Dirigeants de PME dans l'industrie, la construction, l'énergie",
        "Managers HSE dans les PME de marchés émergents",
        "Dirigeants naviguant les réglementations santé-sécurité"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Safety Culture",
        "Mastering the S.A.F.E.T.Y. Framework",
        "HSE Standards & Regulatory Requirements",
        "Hazard Identification & Risk Assessment",
        "Incident Investigation & Root Cause Analysis",
        "Training, Awareness & Behavior Change",
        "AI-Powered HSE Monitoring & Reporting",
        "Final HSE Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la culture de sécurité",
        "Maîtriser le cadre S.A.F.E.T.Y.",
        "Normes HSE et exigences réglementaires",
        "Identification des dangers et évaluation des risques",
        "Investigation d'incidents et analyse des causes racines",
        "Formation, sensibilisation et changement comportemental",
        "Monitoring et reporting HSE augmentés par l'IA",
        "Stratégie HSE finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered HSE: The Future of Safety Management for Small Business",
      fr: "HSE augmenté par l'IA : le futur du management de la sécurité en PME",
      es: null, pt: null
    }
  },
  {
    id: "bc-36",
    slug: "governance-compliance-entrepreneurs",
    intro_video_url: "/__l5e/assets-v1/a62c36e8-29e7-43e5-a2f5-392ffed0ac3f/f0e3c49e799243009da79d1cc66f958d.mp4",
    track: "operations-risk",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 229,
    price_xof: 150300,
    is_new: true,
    title: {
      en: "Governance & Compliance",
      fr: "Gouvernance et conformité",
      es: null, pt: null
    },
    card_tagline: {
      en: "OHADA frameworks and controls",
      fr: "Cadres OHADA et contrôles",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered governance systems, neuroscience-driven ethical decision-making, and high-impact compliance culture",
      fr: "Maîtriser les systèmes de gouvernance augmentés par l'IA, la décision éthique appuyée par les neurosciences, et la culture de conformité à fort impact",
      es: null, pt: null
    },
    framework: "C.O.M.P.L.Y.",
    framework_expansion: {
      en: "Compliance, Oversight, Monitoring, Policies, Legal Protections, Yielding Results",
      fr: "Conformité, Supervision, Monitoring, Politiques, Protections légales, Résultats",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs building governance frameworks",
        "Startup founders forming their first boards",
        "Business owners navigating OHADA corporate law",
        "Leaders strengthening ethical and compliance culture"
      ],
      fr: [
        "Entrepreneurs construisant des cadres de gouvernance",
        "Porteurs de projet startup formant leurs premiers conseils",
        "Dirigeants naviguant le droit des affaires OHADA",
        "Dirigeants renforçant la culture éthique et de conformité"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Ethics & Decision-Making",
        "Mastering the C.O.M.P.L.Y. Framework",
        "Corporate Governance Structures & Board Composition",
        "OHADA Uniform Act & UEMOA Regulatory Context",
        "Policies, Procedures & Internal Controls",
        "Business Fraud Prevention & Financial Risk Management",
        "Data Privacy, Cybersecurity & Compliance Automation",
        "Final Governance Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de l'éthique et de la décision",
        "Maîtriser le cadre C.O.M.P.L.Y.",
        "Structures de gouvernance d'entreprise et composition du conseil",
        "Acte Uniforme OHADA et contexte réglementaire UEMOA",
        "Politiques, procédures et contrôles internes",
        "Prévention de la fraude et gestion du risque financier",
        "Protection des données, cybersécurité et automatisation de la conformité",
        "Stratégie de gouvernance finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Compliance & Governance: A Guide for Entrepreneurs",
      fr: "Conformité et gouvernance augmentées par l'IA : guide pour entrepreneurs",
      es: null, pt: null
    }
  },
  {
    id: "bc-35",
    slug: "building-a-team-hiring-developing-retaining",
    intro_video_url: "/__l5e/assets-v1/147593c5-fbed-4e1a-9cd8-313274c74a9d/602dac8862d147778b05131556e6d89a.mp4",
    track: "operations-risk",
    level: "intermediate",
    duration_weeks: 5,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: {
      en: "Building an A-Team",
      fr: "Construire une équipe A",
      es: null, pt: null
    },
    card_tagline: {
      en: "Hire, develop, retain top talent",
      fr: "Recruter, développer, fidéliser les meilleurs",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered recruitment, neuroscience-driven team development, and high-impact talent retention strategies",
      fr: "Maîtriser le recrutement augmenté par l'IA, le développement d'équipe appuyé par les neurosciences, et les stratégies de fidélisation des talents à fort impact",
      es: null, pt: null
    },
    framework: "H.I.R.E.",
    framework_expansion: {
      en: "Hiring, Integration, Retention, Engagement",
      fr: "Recrutement, Intégration, Rétention, Engagement",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs and business owners building high-performing teams",
        "Startup founders hiring and scaling their workforce",
        "HR professionals adopting AI-powered hiring techniques",
        "Business leaders focused on employee retention and development"
      ],
      fr: [
        "Porteurs de projet et dirigeants construisant des équipes haute performance",
        "Porteurs de projet startup recrutant et scalant leurs effectifs",
        "Professionnels RH adoptant des techniques de recrutement augmentées par l'IA",
        "Dirigeants focalisés sur la fidélisation et le développement des employés"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Hiring & Employee Engagement",
        "Mastering the H.I.R.E. Framework for Business Growth",
        "AI-Powered Recruitment & Smart Talent Acquisition",
        "Creating a High-Performance Workplace Culture",
        "Retention Strategies to Reduce Employee Turnover",
        "Leadership Development & Employee Growth Plans",
        "AI-Powered Employee Productivity & Performance Management",
        "Final Team Building Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences du recrutement et de l'engagement des employés",
        "Maîtriser le cadre H.I.R.E. pour la croissance business",
        "Recrutement augmenté par l'IA et acquisition intelligente de talents",
        "Créer une culture de travail haute performance",
        "Stratégies de fidélisation pour réduire le turnover",
        "Développement du leadership et plans de croissance des employés",
        "Productivité et gestion de la performance augmentées par l'IA",
        "Stratégie finale de construction d'équipe et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Team Building: Hiring, Developing, and Retaining Top Talent",
      fr: "Construction d'équipe augmentée par l'IA : recruter, développer et fidéliser les meilleurs talents",
      es: null, pt: null
    }
  },
  {
    id: "bc-10",
    slug: "negotiation-conflict-resolution",
    intro_video_url: "/__l5e/assets-v1/78d7362f-fda8-45ad-9048-8bfed32de2bf/f0668f190ce4418eaf6f1a8e87845ce5.mp4",
    track: "operations-risk",
    level: "intermediate",
    duration_weeks: 4,
    price_eur: 179,
    price_xof: 117500,
    is_new: true,
    title: {
      en: "Negotiation & Conflict Resolution",
      fr: "Négociation et résolution de conflits",
      es: null, pt: null
    },
    card_tagline: {
      en: "Deal-making and difficult talks",
      fr: "Deal-making et conversations difficiles",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered persuasion, neuroscience-driven negotiation, and high-impact conflict resolution techniques",
      fr: "Maîtriser la persuasion augmentée par l'IA, la négociation appuyée par les neurosciences, et les techniques de résolution de conflits à fort impact",
      es: null, pt: null
    },
    framework: "N.E.G.O.T.I.A.T.E.",
    framework_expansion: {
      en: "Needs, Empathy, Goals, Options, Tactics, Influence, Agreement, Trust, Execute",
      fr: "Besoins, Empathie, Objectifs, Options, Tactiques, Influence, Accord, Confiance, Exécuter",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs negotiating deals and partnerships",
        "Sales leaders handling high-stakes negotiations",
        "Startup founders negotiating with investors",
        "Managers handling workplace conflicts"
      ],
      fr: [
        "Entrepreneurs négociant deals et partenariats",
        "Responsables commerciaux gérant des négociations à fort enjeu",
        "Porteurs de projet startup négociant avec des investisseurs",
        "Managers gérant les conflits en milieu de travail"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Negotiation & Persuasion",
        "Mastering the N.E.G.O.T.I.A.T.E. Framework",
        "The Psychology of Influence & Decision-Making",
        "Handling High-Stakes Business Negotiations",
        "Conflict Resolution & Difficult Conversations",
        "AI & Technology in Modern Negotiations",
        "The Art of Closing Deals & Maintaining Relationships",
        "Final Negotiation Strategy & AI Business Validator"
      ],
      fr: [
        "Les neurosciences de la négociation et de la persuasion",
        "Maîtriser le cadre N.E.G.O.T.I.A.T.E.",
        "La psychologie de l'influence et de la décision",
        "Gérer les négociations business à fort enjeu",
        "Résolution de conflits et conversations difficiles",
        "IA et technologie dans les négociations modernes",
        "L'art du closing et du maintien des relations",
        "Stratégie de négociation finale et AI Business Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Negotiation: The Future of Deal-Making & Conflict Resolution",
      fr: "Négociation augmentée par l'IA : le futur du deal-making et de la résolution de conflits",
      es: null, pt: null
    }
  },
  {
    id: "bc-29",
    slug: "advanced-problem-solving-root-cause",
    intro_video_url: "/__l5e/assets-v1/dc4a28c1-3761-43c6-9b26-89718fd323dc/af39ffc6e66648d9bf7fb9aa9e166bce.mp4",
    track: "operations-risk",
    level: "advanced",
    duration_weeks: 4,
    price_eur: 199,
    price_xof: 130600,
    is_new: true,
    title: {
      en: "Problem Solving & Root Cause Analysis",
      fr: "Résolution de problèmes et analyse des causes racines",
      es: null, pt: null
    },
    card_tagline: {
      en: "Critical thinking and decisions",
      fr: "Pensée critique et décisions",
      es: null, pt: null
    },
    subtitle: {
      en: "Master AI-powered critical thinking, neuroscience-driven decision-making, and high-impact problem-solving strategies",
      fr: "Maîtriser la pensée critique augmentée par l'IA, la décision appuyée par les neurosciences, et les stratégies de résolution de problèmes à fort impact",
      es: null, pt: null
    },
    framework: "S.O.L.V.E.",
    framework_expansion: {
      en: "Structure, Observe, Logic, Validate, Execute",
      fr: "Structurer, Observer, Logique, Valider, Exécuter",
      es: null, pt: null
    },
    designed_for: {
      en: [
        "Entrepreneurs facing complex challenges",
        "Startup founders and professionals seeking faster solutions",
        "Managers needing to improve decision-making",
        "Consultants and analysts handling business issues"
      ],
      fr: [
        "Entrepreneurs faisant face à des défis complexes",
        "Porteurs de projet startup et professionnels cherchant des solutions plus rapides",
        "Managers améliorant leur prise de décision",
        "Consultants et analystes traitant des problématiques business"
      ],
      es: null, pt: null
    },
    modules: {
      en: [
        "The Neuroscience of Problem-Solving & Critical Thinking",
        "Mastering the S.O.L.V.E. Framework for Business Success",
        "Identifying & Eliminating Root Causes in Business Problems",
        "Decision-Making Under Pressure & Crisis Management",
        "AI & Data-Driven Problem-Solving Strategies",
        "The Psychology of Innovation & Creative Thinking",
        "Leading Teams in Problem-Solving & Decision-Making",
        "Final Problem-Solving Strategy & AI Validator"
      ],
      fr: [
        "Les neurosciences de la résolution de problèmes et de la pensée critique",
        "Maîtriser le cadre S.O.L.V.E. pour la réussite business",
        "Identifier et éliminer les causes racines dans les problèmes business",
        "Décision sous pression et gestion de crise",
        "Stratégies de résolution data-driven et augmentées par l'IA",
        "La psychologie de l'innovation et de la pensée créative",
        "Diriger les équipes dans la résolution et la décision",
        "Stratégie finale de résolution et AI Validator"
      ],
      es: null, pt: null
    },
    ebook_title: {
      en: "AI-Powered Problem Solving: How to Find Smart Solutions Faster",
      fr: "Résolution de problèmes augmentée par l'IA : trouver des solutions intelligentes plus vite",
      es: null, pt: null
    }
  }
];

export function getBootcampBySlug(slug: string): Bootcamp | undefined {
  return bootcamps.find((b) => b.slug === slug);
}

